import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { supabase } from '../supabase';
import { daysBetween, FERIADOS_PERU } from '../utils/workingDays';
import useBreakpoint from '../hooks/useBreakpoint';
import { sheet, toastMobile, TAP_TARGET, pageMobile, dialog } from '../mobileStyles';
import BottomSheet from './BottomSheet';

/* Categorías are fetched from the `categorias` table (see Etiquetas view).
   Historical tasks whose categoria no longer exists in the table still render —
   we append their name to the ordered list at runtime. */

/* Alternating category header colors (Excel-style) */
const CAT_HDR_ALT = ['#C97B32', '#4A1219'];

/* Bar/progress colors */
const BAR_WINE   = '#6B1C2A';   // completed (dark wine)
const BAR_LIGHT  = '#D4A0A8';   // remaining portion (muted wine)
const BAR_EMPTY  = '#E8D5D8';   // not started (very light muted)
const CLR_RED    = '#B83B3B';
const CLR_ORANGE = '#C4922A';

/* ── Helpers ── */
function fmtDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}
function toLocal(s) { return s ? new Date(s + 'T00:00:00') : null; }
// D-M-YY format (Excel style): e.g. 6-2-26
function fmtExcel(s) {
  if (!s) return '—';
  const d = toLocal(s);
  const yy = String(d.getFullYear()).slice(-2);
  return `${d.getDate()}-${d.getMonth()+1}-${yy}`;
}
function AsignadoOptions({ options }) {
  // Group by `group` (e.g. "Equipo" vs "Proveedores") using <optgroup>.
  const groups = {};
  for (const o of options) {
    const g = o.group || '—';
    if (!groups[g]) groups[g] = [];
    groups[g].push(o);
  }
  return (
    <>
      {Object.entries(groups).map(([label, list]) => (
        <optgroup key={label} label={label}>
          {list.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </optgroup>
      ))}
    </>
  );
}

function AiInlineList({ title, items, color, bullet, numbered }) {
  const { isMobile } = useBreakpoint();
  const list = Array.isArray(items) ? items : [];
  return (
    <section>
      <h4 style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, fontWeight:700, color, margin:'0 0 4px', textTransform:'uppercase', letterSpacing:'0.06em' }}>
        {title}
      </h4>
      {list.length === 0 ? (
        <p style={{ margin:0, fontSize:12.5, color:'var(--text-light)' }}>—</p>
      ) : numbered ? (
        <ol style={{ margin:0, paddingLeft:22, display:'flex', flexDirection:'column', gap:3 }}>
          {list.map((item, i) => (
            <li key={i} style={isMobile ? { fontSize:15, lineHeight:1.5, color:'var(--text-primary)' } : { fontSize:12.5, lineHeight:1.5, color:'var(--text-primary)' }}>{item}</li>
          ))}
        </ol>
      ) : (
        <ul style={{ margin:0, paddingLeft:0, display:'flex', flexDirection:'column', gap:3, listStyle:'none' }}>
          {list.map((item, i) => (
            <li key={i} style={isMobile ? { fontSize:15, lineHeight:1.5, color:'var(--text-primary)', display:'flex', gap:8 } : { fontSize:12.5, lineHeight:1.5, color:'var(--text-primary)', display:'flex', gap:8 }}>
              <span style={{ color, flexShrink:0, fontWeight:700 }}>{bullet || '•'}</span>
              <span style={{ flex:1, minWidth:0 }}>{item}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function formatAiPanelDate(ymd) {
  if (!ymd) return '';
  const [y, m, d] = String(ymd).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return ymd;
  const dt = new Date(y, m - 1, d);
  const todayLima = new Date(Date.now() - 5 * 3600 * 1000);
  todayLima.setHours(0, 0, 0, 0);
  const sameDay = dt.getFullYear() === todayLima.getFullYear()
    && dt.getMonth() === todayLima.getMonth()
    && dt.getDate() === todayLima.getDate();
  const formatted = dt.toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' });
  return sameDay ? `Hoy, ${formatted}` : formatted;
}

// Compact (< 1024px) helpers — "13 – 19 oct 2026", "28 sep – 4 oct 2026".
function shortMonth(d) {
  return d.toLocaleDateString('es-MX', { month: 'short' }).replace('.', '');
}
function formatCompactRange(a, b) {
  if (a.getFullYear() !== b.getFullYear()) {
    return `${a.getDate()} ${shortMonth(a)} ${a.getFullYear()} – ${b.getDate()} ${shortMonth(b)} ${b.getFullYear()}`;
  }
  if (a.getMonth() !== b.getMonth()) {
    return `${a.getDate()} ${shortMonth(a)} – ${b.getDate()} ${shortMonth(b)} ${b.getFullYear()}`;
  }
  return `${a.getDate()} – ${b.getDate()} ${shortMonth(b)} ${b.getFullYear()}`;
}
function fmtDayMonth(s) {
  if (!s) return '—';
  const d = toLocal(s);
  return `${d.getDate()} ${shortMonth(d)}`;
}

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}


function getProgressLabel(p) {
  if (p == null) return '—';
  if (p === 0) return '0%';
  return `${p}%`;
}
/* Progress pill background — Excel shows solid colored pills */
function getProgressPillStyle(p) {
  if (p == null || p === 0) return { bg:'#E8E2D9', color:'#6B6B6B' };
  if (p === 100) return { bg:'#4A7C59', color:'#fff' };
  if (p >= 60) return { bg:'#C4922A', color:'#fff' };
  return { bg:'#D4A878', color:'#fff' };
}

function computeTimelineRange(tasks) {
  const today = new Date();
  let minD = new Date(today);
  const dated = tasks.filter(t => t.inicio);
  const ends = dated.filter(t => t.fin).map(t => toLocal(t.fin).getTime());
  if (dated.length > 0) {
    const starts = dated.map(t => toLocal(t.inicio).getTime());
    minD = new Date(Math.min(...starts));
  }
  // Snap start to previous Monday
  const startDate = new Date(minD);
  startDate.setDate(startDate.getDate() - ((startDate.getDay() + 6) % 7));
  // End = last task fin date + 1 week; if no tasks, today + 30 days
  let endDate;
  if (ends.length > 0) {
    endDate = new Date(Math.max(...ends));
    endDate.setDate(endDate.getDate() + 7);
  } else {
    endDate = new Date(today);
    endDate.setDate(endDate.getDate() + 30);
  }
  return { startDate, endDate };
}

function buildDays(start, end) {
  const days = [], cur = new Date(start);
  while (cur <= end) {
    days.push({ date: new Date(cur), dateStr: fmtDate(cur), dow: cur.getDay() });
    cur.setDate(cur.getDate() + 1);
  }
  return days;
}

function groupIntoWeeks(days) {
  const weeks = []; let cur = null;
  for (const d of days) {
    if (d.dow === 1 || !cur) { cur = { start: d, days: [] }; weeks.push(cur); }
    cur.days.push(d);
  }
  return weeks;
}

// Day-of-week initial (Spanish: Lunes, Martes, Miércoles, Jueves, Viernes, Sábado, Domingo)
const DOW_INITIAL = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];

/* ── Layout constants ── */
const DAY_W_DEFAULT = 26;
const DAY_W_MIN = 8;
const DAY_W_MAX = 40;
const DAY_W_PRESETS = [
  { key: 'mes',    px: 10, label: 'Mes' },
  { key: 'semana', px: 18, label: 'Semana' },
  { key: 'dia',    px: 26, label: 'Día' },
];
const DAY_LETTERS_MIN = 14; // hide weekday letters when zoom < this
const ROW_H = 32;
const CAT_H = 30;
const HDR_WEEK_H = 22;
const HDR_DOW_H = 20;
const HDR_TOTAL_H = HDR_WEEK_H + HDR_DOW_H;
const DRAG_HANDLE_W = 6;
const BAR_VPAD = 4; // vertical padding inside row

// Left panel column widths
const COL_ACT = 250;
const COL_ASG = 110;
const COL_PROG = 90;   // wider to fit "100% · 6d"
const COL_INI = 72;
const COL_FIN = 72;
const COL_DAYS = 42;
const COL_NOTE = 36;
const LEFT_W = COL_ACT + COL_ASG + COL_PROG + COL_INI + COL_FIN + COL_DAYS + COL_NOTE;

const EMPTY_TASK_FORM = { proyecto_id:'', nombre:'', asignado:'', categoria:'', inicio:'', fin:'', progreso:0, trabaja_sabados:false, trabaja_domingos:false };

/* ================================================================== */
export default function GanttChart({ initialProjectId = null, ganttType = null, initialTaskId = null, onBack = null } = {}) {
  const { isMobile, isCompact } = useBreakpoint();
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(initialProjectId || 'all');
  const [desktopDayW, setDesktopDayW] = useState(() => {
    try {
      const raw = localStorage.getItem('mqm_gantt_zoom');
      const n = raw != null ? Number(raw) : NaN;
      if (Number.isFinite(n) && n >= DAY_W_MIN && n <= DAY_W_MAX) return n;
    } catch (_e) { /* ignore */ }
    return DAY_W_DEFAULT;
  });
  useEffect(() => {
    try { localStorage.setItem('mqm_gantt_zoom', String(desktopDayW)); } catch (_e) { /* ignore */ }
  }, [desktopDayW]);
  // Compact viewports (< 1024px): dayW is derived from the timeline's visible
  // width so a full week ('semana') or ~30 days ('mes') fit. Only the MODE is
  // persisted, under its own key — the desktop key mqm_gantt_zoom is untouched.
  const [compactZoomMode, setCompactZoomMode] = useState(() => {
    try { return localStorage.getItem('mqm_gantt_mobile_view_zoom') === 'mes' ? 'mes' : 'semana'; } catch (_e) { return 'semana'; }
  });
  useEffect(() => {
    try { localStorage.setItem('mqm_gantt_mobile_view_zoom', compactZoomMode); } catch (_e) { /* ignore */ }
  }, [compactZoomMode]);
  // Compact view: 'cronograma' (timeline) | 'lista' (cards).
  const [compactView, setCompactView] = useState(() => {
    try { return localStorage.getItem('mqm_gantt_mobile_view') === 'lista' ? 'lista' : 'cronograma'; } catch (_e) { return 'cronograma'; }
  });
  useEffect(() => {
    try { localStorage.setItem('mqm_gantt_mobile_view', compactView); } catch (_e) { /* ignore */ }
  }, [compactView]);
  // Visible width of the timeline scroller (the task column sits OUTSIDE the
  // scroller, so no sticky-column offset needs subtracting). Set by a ResizeObserver.
  const [compactTimelineW, setCompactTimelineW] = useState(0);
  const compactDayW = compactTimelineW > 0
    ? Math.max(DAY_W_MIN, compactTimelineW / (compactZoomMode === 'mes' ? 30 : 7))
    : (compactZoomMode === 'mes' ? DAY_W_MIN : 24);
  const isCompactRef = useRef(isCompact);
  isCompactRef.current = isCompact;
  const dayW = isCompact ? compactDayW : desktopDayW;
  // Desktop presets / ± / Cmd-Ctrl zoom only; compact zoom is mode-driven.
  const setDayW = useCallback((v) => { if (!isCompactRef.current) setDesktopDayW(v); }, []);
  // Compact viewports: tapping a task row / cell / bar opens this action sheet
  // instead of the desktop hover popover.
  const [taskSheet, setTaskSheet] = useState(null); // { task, dateStr }
  const closeTaskSheet = useCallback(() => setTaskSheet(null), []);
  const adjustZoom = useCallback((delta) => {
    setDayW(prev => Math.max(DAY_W_MIN, Math.min(DAY_W_MAX, prev + delta)));
  }, [setDayW]);
  const showDayLetters = dayW >= DAY_LETTERS_MIN;
  // Cmd/Ctrl + = (also +) to zoom in, Cmd/Ctrl + - to zoom out. Skip when the
  // user is typing into an input/textarea/contentEditable so we don't trample
  // browser-level edit shortcuts.
  useEffect(() => {
    function onKey(e) {
      if (!(e.metaKey || e.ctrlKey)) return;
      if (isCompactRef.current) return; // compact: no custom zoom, leave browser shortcuts alone
      const t = e.target;
      const tag = (t && t.tagName) ? t.tagName.toLowerCase() : '';
      if (tag === 'input' || tag === 'textarea' || (t && t.isContentEditable)) return;
      if (e.key === '=' || e.key === '+') {
        e.preventDefault();
        adjustZoom(2);
      } else if (e.key === '-' || e.key === '_') {
        e.preventDefault();
        adjustZoom(-2);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [adjustZoom]);
  const [loading, setLoading] = useState(true);

  // Per-project observación draft (controlled by selectedProject)
  const [observacionDraft, setObservacionDraft] = useState('');
  const [observacionSaving, setObservacionSaving] = useState(false);
  const [observacionSavedFlash, setObservacionSavedFlash] = useState(false);

  const [journalMap, setJournalMap] = useState({});       // taskId → entries
  // Project-agnostic per-task journal counts. Powers the NOTA column
  // indicator so notes added from the Journal view (which may not have a
  // proyecto_id) still light up the icon next to the matching task.
  const [taskNoteCounts, setTaskNoteCounts] = useState({});
  const [journalByDay, setJournalByDay] = useState({});   // "taskId|YYYY-MM-DD" → entries
  const [allJournals, setAllJournals] = useState([]);
  const [journalModal, setJournalModal] = useState(null); // { task, dateStr? }
  const [journalText, setJournalText] = useState('');
  const [journalAutor, setJournalAutor] = useState('Ximena Li');
  const [journalSaving, setJournalSaving] = useState(false);
  const [journalEntries, setJournalEntries] = useState([]);
  const [journalReady, setJournalReady] = useState(false);
  const [toast, setToast] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [categoriasAll, setCategoriasAll] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  const [showTaskModal, setShowTaskModal] = useState(false);
  // Subtask state. `expandedParents` is persisted so a refresh keeps the
  // same tree view. `subtaskParent` opens the mini-modal scoped to a parent.
  const [expandedParents, setExpandedParents] = useState(() => {
    try {
      const raw = localStorage.getItem('mqm_gantt_expanded');
      const arr = raw ? JSON.parse(raw) : [];
      return new Set(Array.isArray(arr) ? arr : []);
    } catch (_e) { return new Set(); }
  });
  useEffect(() => {
    try { localStorage.setItem('mqm_gantt_expanded', JSON.stringify(Array.from(expandedParents))); } catch (_e) { /* ignore */ }
  }, [expandedParents]);
  const [subtaskParent, setSubtaskParent] = useState(null);
  const [subtaskForm, setSubtaskForm] = useState({ nombre: '', asignado: '', inicio: '', fin: '' });
  const [subtaskSaving, setSubtaskSaving] = useState(false);
  const [hoverParentId, setHoverParentId] = useState(null);
  const [taskForm, setTaskForm] = useState(EMPTY_TASK_FORM);
  const [taskSaving, setTaskSaving] = useState(false);

  const [editTask, setEditTask] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);

  // Cell hover popover + mini progress modal
  const [cellPopover, setCellPopover] = useState(null); // { task, dateStr, x, y, above }
  const cellPopoverTimer = useRef(null);
  const cellPopoverCloseTimer = useRef(null);
  const cellPopoverRef = useRef(null);
  const [progressModal, setProgressModal] = useState(null); // { task, value }
  const [aiLoading, setAiLoading] = useState(false);
  const [panelSummary, setPanelSummary] = useState(null);
  const [panelLoading, setPanelLoading] = useState(false);
  const [panelCollapsed, setPanelCollapsed] = useState(false);
  const [manualEntries, setManualEntries] = useState([]);
  const [progressSaving, setProgressSaving] = useState(false);

  const scrollRef = useRef(null);
  const exportRef = useRef(null);
  const [pdfExporting, setPdfExporting] = useState(false);
  const isPanning = useRef(false);
  const panStartX = useRef(0);
  const panScrollLeft = useRef(0);

  const barDrag = useRef(null);
  const dragPreviewRef = useRef(null);
  const [dragPreview, setDragPreview] = useState(null);

  const [visibleRange, setVisibleRange] = useState('');

  // Inline date editing in the INICIO/FIN left-panel columns.
  const [editingDate, setEditingDate] = useState(null); // { taskId, field }
  const [dateDraft, setDateDraft] = useState('');
  const [dateSaving, setDateSaving] = useState(false);
  const skipDateBlurRef = useRef(false);

  function showToast(message, type = 'success') {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  }

  /* ── Fetchers ── */
  const fetchProjects = useCallback(async () => {
    const { data } = await supabase.from('proyectos').select('*').order('nombre');
    if (data) setProjects(data);
  }, []);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    let q = supabase.from('tareas').select('*, proyectos(nombre)').order('inicio', { nullsFirst: false });
    if (selectedProject !== 'all') q = q.eq('proyecto_id', selectedProject);
    if (ganttType) q = q.eq('tipo', ganttType);
    const { data, error } = await q;
    if (!error && data) setTasks(data);
    setLoading(false);
  }, [selectedProject, ganttType]);

  const fetchJournals = useCallback(async () => {
    let q = supabase.from('journal').select('*').order('created_at', { ascending: false });
    if (selectedProject !== 'all') q = q.eq('proyecto_id', selectedProject);
    const { data, error } = await q;
    if (error) { setJournalReady(false); return; }
    setJournalReady(true);
    setAllJournals(data || []);
    const map = {}; const byDay = {};
    for (const j of (data || [])) {
      if (!map[j.tarea_id]) map[j.tarea_id] = [];
      map[j.tarea_id].push(j);
      const k = `${j.tarea_id}|${j.fecha}`;
      if (!byDay[k]) byDay[k] = [];
      byDay[k].push(j);
    }
    setJournalMap(map); setJournalByDay(byDay);
  }, [selectedProject]);

  // Independent count fetch — pulls just (tarea_id) for every journal row
  // and rolls it up to { tarea_id: count }. Runs against the whole table
  // (no proyecto_id filter) so journal notes saved without a proyecto_id
  // still surface on the matching Gantt task row.
  const fetchTaskNoteCounts = useCallback(async () => {
    const { data, error } = await supabase
      .from('journal')
      .select('tarea_id')
      .not('tarea_id', 'is', null);
    if (error) return;
    const counts = {};
    for (const row of (data || [])) {
      if (!row.tarea_id) continue;
      counts[row.tarea_id] = (counts[row.tarea_id] || 0) + 1;
    }
    setTaskNoteCounts(counts);
  }, []);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);
  useEffect(() => { fetchTasks(); fetchJournals(); fetchTaskNoteCounts(); }, [fetchTasks, fetchJournals, fetchTaskNoteCounts]);

  // Scroll the initially-focused task into view once tasks have loaded,
  // then briefly flash the row so the user can spot it.
  useEffect(() => {
    if (!initialTaskId || loading) return;
    if (!tasks.some(t => t.id === initialTaskId)) return;
    const id = requestAnimationFrame(() => {
      const el = document.querySelector(`[data-task-row="${initialTaskId}"]`);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const prevBg = el.style.background;
      el.style.background = 'rgba(201,169,110,0.25)';
      el.style.transition = 'background 0.6s ease';
      setTimeout(() => { el.style.background = prevBg; }, 1400);
    });
    return () => cancelAnimationFrame(id);
  }, [initialTaskId, loading, tasks]);
  useEffect(() => { if (initialProjectId) setSelectedProject(initialProjectId); }, [initialProjectId]);

  // Persist the Gantt's internal project picker so reloading (or leaving
  // + coming back) restores the last choice even when App doesn't know
  // about it yet.
  useEffect(() => {
    try {
      if (selectedProject && selectedProject !== 'all') localStorage.setItem('mqm_gantt_project', selectedProject);
      else localStorage.removeItem('mqm_gantt_project');
    } catch { /* ignore */ }
  }, [selectedProject]);


  useEffect(() => {
    if (!cellPopover) return;
    function onDocDown(e) {
      if (cellPopoverRef.current && !cellPopoverRef.current.contains(e.target)) {
        setCellPopover(null);
      }
    }
    function onKey(e) { if (e.key === 'Escape') setCellPopover(null); }
    document.addEventListener('mousedown', onDocDown);
    document.addEventListener('touchstart', onDocDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocDown);
      document.removeEventListener('touchstart', onDocDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [cellPopover]);

  // Team members for Asignado dropdown
  const fetchTeam = useCallback(async () => {
    const { data } = await supabase.from('equipo').select('id, nombre, cargo, color').order('nombre');
    if (data) setTeamMembers(data);
  }, []);
  useEffect(() => { fetchTeam(); }, [fetchTeam]);

  const fetchCategorias = useCallback(async () => {
    const { data } = await supabase.from('categorias').select('id, nombre, tipo, color').order('nombre');
    if (data) setCategoriasAll(data);
  }, []);
  const fetchProveedores = useCallback(async () => {
    const { data } = await supabase.from('proveedores').select('id, nombre, especialidad, activo').eq('activo', true).order('nombre');
    if (data) setProveedores(data);
  }, []);
  // Manual entries scoped to the current gantt type (diseño/obra). Used by the
  // "Recomendaciones del manual" checklist panel inside the Nueva Tarea modal.
  const fetchManualEntries = useCallback(async () => {
    let q = supabase
      .from('manuales')
      .select('id, tipo, titulo, descripcion, categoria, tipo_entrada, recomendacion_ia')
      .eq('activo', true)
      .order('created_at', { ascending: false });
    if (ganttType) q = q.eq('tipo', ganttType);
    const { data, error } = await q;
    if (error) { setManualEntries([]); return; }
    setManualEntries(data || []);
  }, [ganttType]);
  useEffect(() => { fetchCategorias(); fetchProveedores(); fetchManualEntries(); }, [fetchCategorias, fetchProveedores, fetchManualEntries]);

  // Manual entries that match the category currently chosen in the New Task
  // modal. Limited to those with an AI recommendation (the actionable line).
  const manualRecsForTaskForm = useMemo(() => {
    const cat = (taskForm?.categoria || '').trim().toLowerCase();
    if (!cat) return [];
    return manualEntries
      .filter(m => (m.categoria || '').trim().toLowerCase() === cat)
      .filter(m => m.recomendacion_ia)
      .slice(0, 3);
  }, [manualEntries, taskForm.categoria]);

  // Filter categorias by the current gantt type (diseño/obra/null → show all).
  const categoriasForView = useMemo(() => {
    if (!ganttType) return categoriasAll;
    return categoriasAll.filter(c => (c.tipo || 'obra') === ganttType);
  }, [categoriasAll, ganttType]);

  // Ordered keys for grouping / dropdowns:
  //  • Start with the in-view categorias (filtered by ganttType) in nombre order.
  //  • Append any category name actually used by a task in the current view
  //    that isn't in the categorias table, so historical rows still group.
  const ORDERED_KEYS = useMemo(() => {
    const keys = categoriasForView.map(c => c.nombre);
    const seen = new Set(keys);
    // `tasks` is already filtered by tipo at fetch time — no need to
    // re-classify by categoria regex here.
    for (const t of tasks) {
      if (!t.categoria) continue;
      if (!seen.has(t.categoria)) { seen.add(t.categoria); keys.push(t.categoria); }
    }
    return keys;
  }, [categoriasForView, tasks]);

  // Asignado options: always equipo; add activo proveedores when gantt is Obra.
  const asignadoOptions = useMemo(() => {
    const members = teamMembers.map(m => ({
      value: m.nombre,
      label: `${getInitials(m.nombre)} · ${m.nombre}${m.cargo ? ` (${m.cargo})` : ''}`,
      group: 'Equipo',
    }));
    if (ganttType === 'obra') {
      const provs = proveedores.map(p => ({
        value: p.nombre,
        label: `${getInitials(p.nombre)} · ${p.nombre}${p.especialidad ? ` (${p.especialidad})` : ''}`,
        group: 'Proveedores',
      }));
      return [...members, ...provs];
    }
    return members;
  }, [teamMembers, proveedores, ganttType]);

  /* ── Timeline ── */
  // `tasks` is already tipo-filtered at fetch (fetchTasks / refetchTasksForProject
  // apply .eq('tipo', ganttType) when ganttType is set), so we feed it directly.
  const { startDate, endDate } = useMemo(() => computeTimelineRange(tasks), [tasks]);
  const allDays = useMemo(() => buildDays(startDate, endDate), [startDate, endDate]);
  const weekGroups = useMemo(() => groupIntoWeeks(allDays), [allDays]);
  const timelineWidth = allDays.length * dayW;
  const todayStr = fmtDate(new Date());
  const todayIdx = allDays.findIndex(d => d.dateStr === todayStr);

  const dayIndexMap = useMemo(() => {
    const m = {}; allDays.forEach((d, i) => { m[d.dateStr] = i; }); return m;
  }, [allDays]);

  /* ── Tasks in view ──
     `tasks` is already filtered by tipo at fetch time. Search narrows it
     further against activity name (actividad/nombre) and asignado. */
  const filteredTasks = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return tasks;
    return tasks.filter(t =>
      (t.actividad || t.nombre || '').toLowerCase().includes(q) ||
      (t.asignado || '').toLowerCase().includes(q)
    );
  }, [tasks, searchQuery]);

  /* ── Group tasks ── */
  // Top-level rows go into their category bucket. Subtasks (parent_id set
  // OR es_subtarea === true) are grouped separately under their parent and
  // injected into the render via `rowsForCategory` when expanded.
  const grouped = useMemo(() => {
    const g = {}; for (const k of ORDERED_KEYS) g[k] = [];
    for (const t of filteredTasks) {
      if (t.parent_id || t.es_subtarea) continue;
      if (!g[t.categoria]) g[t.categoria] = [];
      g[t.categoria].push(t);
    }
    return g;
  }, [filteredTasks, ORDERED_KEYS]);

  // parent_id → subtasks[]. Built from the unfiltered task list so subtasks
  // remain visible even when the search query excludes them (we always show
  // every subtask of a visible parent so the tree stays coherent).
  const subtasksByParent = useMemo(() => {
    const map = {};
    for (const t of tasks) {
      const pid = t.parent_id;
      if (!pid) continue;
      if (!map[pid]) map[pid] = [];
      map[pid].push(t);
    }
    for (const list of Object.values(map)) {
      list.sort((a, b) => {
        const ai = a.inicio || '';
        const bi = b.inicio || '';
        if (ai !== bi) return ai.localeCompare(bi);
        return (a.nombre || '').localeCompare(b.nombre || '');
      });
    }
    return map;
  }, [tasks]);

  // Helpers for the parent/subtask tree.
  const isExpanded = useCallback(
    (taskId) => expandedParents.has(taskId),
    [expandedParents],
  );
  const toggleExpanded = useCallback((taskId) => {
    setExpandedParents(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId); else next.add(taskId);
      return next;
    });
  }, []);
  // For each parent, count completed (progreso === 100) vs total subtasks.
  const subtaskStats = useCallback((parentId) => {
    const list = subtasksByParent[parentId] || [];
    const total = list.length;
    const done = list.filter(s => (s.progreso || 0) >= 100).length;
    return { total, done };
  }, [subtasksByParent]);
  // Auto progress from subtasks (rounded). Falls back to the task's own
  // stored progreso when it has no subtasks.
  const effectiveProgress = useCallback((task) => {
    const stats = subtaskStats(task.id);
    if (stats.total === 0) return task.progreso ?? 0;
    return Math.round((stats.done / stats.total) * 100);
  }, [subtaskStats]);

  // Visible rows per category (parent + expanded subtasks). Used for both
  // the left panel and the timeline so they stay vertically aligned.
  const rowsForCategory = useCallback((cat) => {
    const out = [];
    for (const task of (grouped[cat] || [])) {
      out.push({ task, isSubtask: false });
      if (isExpanded(task.id)) {
        for (const sub of (subtasksByParent[task.id] || [])) {
          out.push({ task: sub, isSubtask: true, parentId: task.id });
        }
      }
    }
    return out;
  }, [grouped, isExpanded, subtasksByParent]);
  // Sort categories by the earliest task `inicio` within each group (ascending).
  // Categories with no dated tasks fall back to +Infinity so they sink to the
  // bottom. Tiebreaker: original ORDERED_KEYS position (categorias-table order
  // first, then first-seen order for orphan categorías).
  const activeCategories = useMemo(() => {
    const orderIndex = new Map(ORDERED_KEYS.map((k, i) => [k, i]));
    const minInicio = (cat) => {
      const rows = grouped[cat] || [];
      let min = Infinity;
      for (const t of rows) {
        if (!t.inicio) continue;
        const ms = toLocal(t.inicio).getTime();
        if (Number.isFinite(ms) && ms < min) min = ms;
      }
      return min;
    };
    return ORDERED_KEYS
      .filter(k => grouped[k] && grouped[k].length > 0)
      .sort((a, b) => {
        const da = minInicio(a);
        const db = minInicio(b);
        if (da !== db) return da - db;
        return (orderIndex.get(a) ?? 0) - (orderIndex.get(b) ?? 0);
      });
  }, [ORDERED_KEYS, grouped]);

  const selectedProj = projects.find(p => p.id === selectedProject);
  const projectName = selectedProj?.nombre || (selectedProject === 'all' ? '—' : '—');

  // Reset the observación draft whenever the selected project (or its stored
  // observación) changes so we don't display stale text from another project.
  useEffect(() => {
    setObservacionDraft(selectedProj?.observacion || '');
    setObservacionSavedFlash(false);
  }, [selectedProject, selectedProj?.observacion]);

  async function saveObservacion() {
    if (!selectedProj) return;
    const trimmed = (observacionDraft || '').trim();
    const normalized = trimmed === '' ? null : trimmed;
    const current = selectedProj.observacion ?? null;
    if (normalized === current) return;
    setObservacionSaving(true);
    const { data, error } = await supabase
      .from('proyectos')
      .update({ observacion: normalized })
      .eq('id', selectedProj.id)
      .select();
    setObservacionSaving(false);
    if (error) {
      if (/column .* does not exist/i.test(error.message || '')) {
        showToast('Falta la columna observacion — ejecuta ALTER TABLE proyectos ADD COLUMN observacion text', 'error');
      } else {
        showToast('Error guardando observación: ' + error.message, 'error');
      }
      return;
    }
    if (!data || data.length === 0) {
      showToast('No se guardó la observación (posible bloqueo RLS en proyectos)', 'error');
      return;
    }
    setProjects(prev => prev.map(p => p.id === selectedProj.id ? { ...p, observacion: normalized } : p));
    setObservacionSavedFlash(true);
    setTimeout(() => setObservacionSavedFlash(false), 1800);
  }

  /* ── Export Gantt as PDF ── */
  async function exportPDF() {
    if (pdfExporting) return;
    const root = exportRef.current;
    if (!root) return;
    setPdfExporting(true);

    const rightPanel = root.querySelector('[data-gantt-right]');
    const savedRight = rightPanel ? {
      overflow: rightPanel.style.overflow,
      overflowX: rightPanel.style.overflowX,
      overflowY: rightPanel.style.overflowY,
      flex: rightPanel.style.flex,
      width: rightPanel.style.width,
      minWidth: rightPanel.style.minWidth,
      cursor: rightPanel.style.cursor,
    } : null;
    const savedRoot = {
      width: root.style.width,
      maxWidth: root.style.maxWidth,
    };

    if (rightPanel) {
      rightPanel.style.overflow = 'visible';
      rightPanel.style.overflowX = 'visible';
      rightPanel.style.overflowY = 'visible';
      rightPanel.style.flex = 'none';
      rightPanel.style.width = `${timelineWidth}px`;
      rightPanel.style.minWidth = `${timelineWidth}px`;
      rightPanel.style.cursor = 'default';
    }
    root.style.width = 'max-content';
    root.style.maxWidth = 'none';
    root.classList.add('gantt-export-mode');

    // Allow layout to settle.
    await new Promise(r => setTimeout(r, 120));

    try {
      const canvas = await html2canvas(root, {
        scale: 2,
        backgroundColor: '#FDFBF8',
        useCORS: true,
        logging: false,
        windowWidth: root.scrollWidth,
        windowHeight: root.scrollHeight,
        width: root.scrollWidth,
        height: root.scrollHeight,
      });

      const cw = canvas.width;
      const ch = canvas.height;
      const aspect = cw / ch;

      // A3 landscape = 420×297mm; A2 landscape = 594×420mm. Pick A2 for very wide projects.
      const useA2 = aspect > 1.7;
      const format = useA2 ? 'a2' : 'a3';
      const pageW = useA2 ? 594 : 420;
      const pageH = useA2 ? 420 : 297;

      const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format });
      const margin = 8;
      const availW = pageW - margin * 2;
      const availH = pageH - margin * 2;

      let imgW = availW;
      let imgH = imgW / aspect;
      if (imgH > availH) {
        imgH = availH;
        imgW = imgH * aspect;
      }
      const x = (pageW - imgW) / 2;
      const y = (pageH - imgH) / 2;

      const imgData = canvas.toDataURL('image/png');
      pdf.addImage(imgData, 'PNG', x, y, imgW, imgH, undefined, 'FAST');

      const dateStr = new Date().toISOString().split('T')[0];
      const safeName = (projectName || 'Proyecto').toString().replace(/[^\w-]+/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '') || 'Proyecto';
      const safeType = ganttType ? (ganttType === 'diseño' ? 'Diseno' : 'Obra') : 'General';
      pdf.save(`Gantt_${safeName}_${safeType}_${dateStr}.pdf`);

      showToast('PDF descargado correctamente');
    } catch (e) {
      console.error('[gantt] pdf export failed:', e);
      showToast('Error al generar PDF: ' + (e?.message || 'desconocido'), 'error');
    } finally {
      if (rightPanel && savedRight) {
        rightPanel.style.overflow = savedRight.overflow;
        rightPanel.style.overflowX = savedRight.overflowX;
        rightPanel.style.overflowY = savedRight.overflowY;
        rightPanel.style.flex = savedRight.flex;
        rightPanel.style.width = savedRight.width;
        rightPanel.style.minWidth = savedRight.minWidth;
        rightPanel.style.cursor = savedRight.cursor;
      }
      root.style.width = savedRoot.width;
      root.style.maxWidth = savedRoot.maxWidth;
      root.classList.remove('gantt-export-mode');
      setPdfExporting(false);
    }
  }

  const architect = selectedProj?.arquitecta_nombre || selectedProj?.arquitecta || '— Sin asignar';
  const projectStart = selectedProj?.inicio || (tasks[0]?.inicio ?? null);
  const weekToShow = todayIdx >= 0 ? Math.floor(todayIdx / 7) + 1 : 1;

  // Real cronograma duration — earliest task inicio → latest task fin, then
  // count working days (weekends + FERIADOS_PERU excluded) without double-
  // counting overlaps. `tasks` is already filtered by project + tipo at
  // fetch time, so this naturally scopes to the current gantt view.
  const durationSummary = useMemo(() => {
    let earliest = null;
    let latest = null;
    for (const t of tasks) {
      if (t.inicio && (!earliest || t.inicio < earliest)) earliest = t.inicio;
      if (t.fin && (!latest || t.fin > latest)) latest = t.fin;
    }
    if (!earliest || !latest) return { earliest: null, latest: null, workingDays: 0 };
    const wd = daysBetween(earliest, latest, false, false);
    return { earliest, latest, workingDays: Number.isFinite(wd) ? wd : 0 };
  }, [tasks]);

  /* ── Visible range ── */
  const updateVisibleRange = useCallback(() => {
    const el = scrollRef.current;
    if (!el || allDays.length === 0) return;
    const li = Math.max(0, Math.floor(el.scrollLeft / dayW));
    const ri = Math.min(li + Math.floor(el.clientWidth / dayW), allDays.length - 1);
    const l = allDays[li]; const r = allDays[ri];
    if (l && r) {
      const f = d => d.date.toLocaleDateString('es-MX', { day:'numeric', month:'short' });
      setVisibleRange(`${f(l)} — ${f(r)}`);
    }
  }, [allDays, dayW]);

  useEffect(() => {
    const el = scrollRef.current; if (!el) return;
    const h = () => updateVisibleRange();
    el.addEventListener('scroll', h, { passive: true });
    const t = setTimeout(h, 150);
    return () => { el.removeEventListener('scroll', h); clearTimeout(t); };
  }, [updateVisibleRange, loading]);

  useEffect(() => {
    if (loading || todayIdx < 0) return;
    if (isCompactRef.current) return; // compact positions the week itself (below)
    const el = scrollRef.current; if (!el) return;
    requestAnimationFrame(() => {
      el.scrollLeft = Math.max(0, todayIdx * dayW - el.clientWidth / 2);
      updateVisibleRange();
    });
  }, [loading, todayIdx, updateVisibleRange, dayW]);

  /* ── Compact timeline: measure, week positioning, visible-range label ── */
  const hasCompactTimeline = isCompact && compactView === 'cronograma' && !loading && filteredTasks.length > 0;
  const [compactRange, setCompactRange] = useState('');
  const compactPendingTodayRef = useRef(true);
  const compactFirstIdxRef = useRef(null);

  useEffect(() => {
    if (!hasCompactTimeline) return undefined;
    const el = scrollRef.current; if (!el) return undefined;
    const measure = () => setCompactTimelineW(el.clientWidth);
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [hasCompactTimeline]);

  // Leaving the timeline (Lista, reload, empty) → next open jumps to today's week.
  useEffect(() => {
    if (!hasCompactTimeline) { compactPendingTodayRef.current = true; compactFirstIdxRef.current = null; }
  }, [hasCompactTimeline]);

  const updateCompactRange = useCallback(() => {
    const el = scrollRef.current;
    if (!el || allDays.length === 0 || !(dayW > 0)) return;
    const last = allDays.length - 1;
    const li = Math.max(0, Math.min(last, Math.round(el.scrollLeft / dayW)));
    const span = Math.max(1, Math.round(el.clientWidth / dayW));
    const ri = Math.max(li, Math.min(last, li + span - 1));
    compactFirstIdxRef.current = li;
    setCompactRange(formatCompactRange(allDays[li].date, allDays[ri].date));
  }, [allDays, dayW]);

  // Position without animation: today's Monday on first open, otherwise keep
  // the first visible day when dayW changes (resize / Semana↔Mes).
  useLayoutEffect(() => {
    if (!hasCompactTimeline || compactTimelineW <= 0) return;
    const el = scrollRef.current; if (!el) return;
    let idx;
    if (compactPendingTodayRef.current || compactFirstIdxRef.current == null) {
      const base = todayIdx >= 0 ? todayIdx : 0;
      const dow = allDays[base] ? allDays[base].dow : 1;
      idx = Math.max(0, base - ((dow + 6) % 7));
      compactPendingTodayRef.current = false;
    } else {
      idx = compactFirstIdxRef.current;
    }
    el.scrollLeft = idx * dayW;
    compactFirstIdxRef.current = idx;
    updateCompactRange();
  }, [hasCompactTimeline, compactTimelineW, dayW, todayIdx, allDays, updateCompactRange]);

  useEffect(() => {
    if (!hasCompactTimeline) return undefined;
    const el = scrollRef.current; if (!el) return undefined;
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => { raf = 0; updateCompactRange(); });
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => { el.removeEventListener('scroll', onScroll); if (raf) cancelAnimationFrame(raf); };
  }, [hasCompactTimeline, updateCompactRange]);

  function compactStepWeek(dir) {
    scrollRef.current?.scrollBy({ left: dir * 7 * dayW, behavior: 'smooth' });
  }
  function compactScrollToday() {
    if (todayIdx < 0) return;
    scrollRef.current?.scrollTo({ left: todayIdx * dayW, behavior: 'smooth' });
  }

  function getBarPixels(startStr, endStr) {
    if (!startStr || !endStr) return null;
    const si = dayIndexMap[startStr]; const ei = dayIndexMap[endStr];
    if (si == null && ei == null) return null;
    const s = si != null ? si : 0;
    const e = ei != null ? ei : allDays.length - 1;
    return { left: s * dayW, width: Math.max((e - s + 1) * dayW, dayW) };
  }

  /* ── Bar drag ── */
  function onBarMouseDown(e, task, mode) {
    e.stopPropagation();
    e.preventDefault();
    if (!task.inicio || !task.fin) return;
    barDrag.current = {
      taskId: task.id, mode, startX: e.clientX,
      origInicio: task.inicio, origFin: task.fin,
      scrollLeftAtStart: scrollRef.current?.scrollLeft || 0,
      hasMoved: false,
    };
    dragPreviewRef.current = null;
    document.body.style.cursor = mode === 'move' ? 'grabbing' : 'col-resize';
    document.body.style.userSelect = 'none';
  }

  useEffect(() => {
    function onMove(e) {
      if (barDrag.current) {
        const bd = barDrag.current;
        bd.hasMoved = true;
        const scrollDelta = (scrollRef.current?.scrollLeft || 0) - bd.scrollLeftAtStart;
        const dx = e.clientX - bd.startX + scrollDelta;
        const dayDelta = Math.round(dx / dayW);
        const origSi = dayIndexMap[bd.origInicio] ?? 0;
        const origEi = dayIndexMap[bd.origFin] ?? origSi;
        let newSi, newEi;
        if (bd.mode === 'move') { newSi = origSi + dayDelta; newEi = origEi + dayDelta; }
        else if (bd.mode === 'left') { newSi = origSi + dayDelta; newEi = origEi; if (newSi > newEi) newSi = newEi; }
        else { newSi = origSi; newEi = origEi + dayDelta; if (newEi < newSi) newEi = newSi; }
        newSi = Math.max(0, Math.min(newSi, allDays.length - 1));
        newEi = Math.max(0, Math.min(newEi, allDays.length - 1));
        const preview = { taskId: bd.taskId, inicio: allDays[newSi]?.dateStr, fin: allDays[newEi]?.dateStr, x: e.clientX, y: e.clientY };
        dragPreviewRef.current = preview;
        setDragPreview(preview);
        return;
      }
      if (isPanning.current && scrollRef.current) {
        scrollRef.current.scrollLeft = panScrollLeft.current - (e.pageX - panStartX.current);
      }
    }
    function onUp() {
      if (barDrag.current) {
        const bd = barDrag.current;
        const preview = dragPreviewRef.current;
        barDrag.current = null; dragPreviewRef.current = null;
        document.body.style.cursor = ''; document.body.style.userSelect = '';
        setDragPreview(null);
        if (preview && bd.hasMoved && (preview.inicio !== bd.origInicio || preview.fin !== bd.origFin)) {
          saveDraggedDatesRef.current?.(bd.taskId, preview.inicio, preview.fin);
        }
        return;
      }
      if (isPanning.current) {
        isPanning.current = false;
        document.body.style.cursor = ''; document.body.style.userSelect = '';
      }
    }
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
  }, [dayIndexMap, allDays, dayW]);

  const saveDraggedDatesRef = useRef(null);
  saveDraggedDatesRef.current = async function saveDraggedDates(taskId, newInicio, newFin) {
    // Optimistic update so the bar lands instantly on mouse-up.
    setTasks(prev => prev.map(t => t.id === taskId ? { ...t, inicio: newInicio, fin: newFin } : t));
    const { error } = await supabase.from('tareas').update({ inicio: newInicio, fin: newFin }).eq('id', taskId);
    if (error) showToast('Error al guardar fecha', 'error');
    // Always pull DB truth after a write so the UI mirrors Supabase
    // (also fixes silent RLS no-ops — state would re-snap on refetch).
    await fetchTasks();
  };

  /* ── Inline INICIO/FIN date editing (left panel) ── */
  function startInlineDate(task, field) {
    if (editingDate) return; // one cell at a time
    skipDateBlurRef.current = false;
    setDateDraft(task[field] || '');
    setEditingDate({ taskId: task.id, field });
  }
  function cancelInlineDate() {
    skipDateBlurRef.current = true; // suppress the blur that fires on unmount
    setEditingDate(null);
    setDateDraft('');
  }
  async function saveInlineDate(task, field) {
    const value = dateDraft;
    const current = task[field] || '';
    if (!value || value === current) { setEditingDate(null); setDateDraft(''); return; }
    setDateSaving(true);
    // Optimistic update so the bar re-positions instantly.
    setTasks(prev => prev.map(t => t.id === task.id ? { ...t, [field]: value } : t));
    const { error } = await supabase.from('tareas').update({ [field]: value }).eq('id', task.id);
    setDateSaving(false);
    setEditingDate(null);
    setDateDraft('');
    if (error) { showToast('Error al guardar fecha', 'error'); }
    else { showToast('Fecha actualizada'); }
    // Pull DB truth so the bar mirrors Supabase (also catches silent RLS no-ops).
    await fetchTasks();
  }
  function onInlineDateBlur(task, field) {
    if (skipDateBlurRef.current) { skipDateBlurRef.current = false; return; }
    saveInlineDate(task, field);
  }
  // Renders an INICIO/FIN cell that becomes a native date picker on click.
  function editableDateCell(task, field, opts = {}) {
    const { width, fontSize = 11, color } = opts;
    const isEditing = editingDate && editingDate.taskId === task.id && editingDate.field === field;
    const baseStyle = { ...Z.dataCell, width, justifyContent: 'center', fontSize, ...(color ? { color } : {}) };
    if (isEditing) {
      return (
        <div style={{ ...baseStyle, padding: 0, position: 'relative' }} onClick={(e) => e.stopPropagation()}>
          <input
            type="date"
            autoFocus
            value={dateDraft}
            disabled={dateSaving}
            onChange={(e) => setDateDraft(e.target.value)}
            onBlur={() => onInlineDateBlur(task, field)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
              else if (e.key === 'Escape') { e.preventDefault(); cancelInlineDate(); }
            }}
            style={{
              width: '100%', border: 'none', background: 'transparent',
              fontSize: 11, fontFamily: 'inherit', textAlign: 'center',
              color: 'var(--text-primary)', padding: '0 2px',
              cursor: dateSaving ? 'wait' : 'text', opacity: dateSaving ? 0.6 : 1,
            }}
          />
          {dateSaving && (
            <span style={{ position: 'absolute', right: 2, top: '50%', transform: 'translateY(-50%)', fontSize: 9, pointerEvents: 'none' }}>⏳</span>
          )}
        </div>
      );
    }
    return (
      <div
        style={{ ...baseStyle, cursor: 'pointer' }}
        onClick={(e) => { e.stopPropagation(); startInlineDate(task, field); }}
        title="Editar fecha"
      >
        {fmtExcel(task[field])}
      </div>
    );
  }

  function onPanMouseDown(e) {
    if (e.target.closest('[data-barzone]')) return;
    isPanning.current = true;
    panStartX.current = e.pageX;
    panScrollLeft.current = scrollRef.current?.scrollLeft || 0;
    document.body.style.cursor = 'grabbing';
    document.body.style.userSelect = 'none';
  }

  function scrollToToday() {
    if (todayIdx < 0) return;
    scrollRef.current?.scrollTo({ left: Math.max(0, todayIdx * dayW - (scrollRef.current?.clientWidth || 0) / 2), behavior:'smooth' });
  }
  function jumpWeeks(dir) { scrollRef.current?.scrollBy({ left: dir * 28 * dayW, behavior:'smooth' }); }

  /* ── AI summary ── */
  function todayLimaYmd() {
    const d = new Date();
    const lima = new Date(d.getTime() - 5 * 3600 * 1000);
    return lima.toISOString().slice(0, 10);
  }
  async function loadExistingSummary(projectId) {
    const { data, error } = await supabase
      .from('resumenes_diarios')
      .select('*')
      .eq('proyecto_id', projectId)
      .eq('fecha', todayLimaYmd())
      .maybeSingle();
    if (error) return null;
    return data;
  }

  // Reload the inline Resumen IA panel for the current project. Picks today's
  // row if present, otherwise the most recent prior row so the panel still has
  // content to show before the user (or the cron) generates a fresh one.
  const loadPanelSummary = useCallback(async (projectId) => {
    if (!projectId || projectId === 'all') { setPanelSummary(null); return; }
    setPanelLoading(true);
    try {
      const today = todayLimaYmd();
      const { data: todayRow } = await supabase
        .from('resumenes_diarios')
        .select('*')
        .eq('proyecto_id', projectId)
        .eq('fecha', today)
        .maybeSingle();
      if (todayRow) { setPanelSummary(todayRow); return; }
      const { data: latest } = await supabase
        .from('resumenes_diarios')
        .select('*')
        .eq('proyecto_id', projectId)
        .order('fecha', { ascending: false })
        .limit(1)
        .maybeSingle();
      setPanelSummary(latest || null);
    } catch (_e) {
      setPanelSummary(null);
    } finally {
      setPanelLoading(false);
    }
  }, []);

  // Auto-load today's (or the most recent) AI summary for the inline panel
  // whenever the selected project changes.
  useEffect(() => {
    loadPanelSummary(selectedProject);
  }, [selectedProject, loadPanelSummary]);

  async function generateAiSummary(projectId, force) {
    setAiLoading(true);
    try {
      if (!force) {
        const existing = await loadExistingSummary(projectId);
        if (existing) {
          setPanelSummary(existing);
          setAiLoading(false);
          return;
        }
      }
      const res = await fetch('/api/daily-summary', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ proyecto_id: projectId }),
      });
      const text = await res.text();
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}${text ? ` — ${text.slice(0, 300)}` : ''}`);
      }
      const data = text ? JSON.parse(text) : {};
      const summary = data.summary || data;
      setPanelSummary(summary);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[gantt] AI summary failed', e.message);
      showToast('Error al generar resumen IA: ' + (e.message || 'error'), 'error');
    } finally {
      setAiLoading(false);
    }
  }
  /* ── Cell popover (click or 1.5s hover) ── */
  function showCellPopoverNow(task, dateStr, cellEl) {
    if (cellPopoverTimer.current) { clearTimeout(cellPopoverTimer.current); cellPopoverTimer.current = null; }
    if (cellPopoverCloseTimer.current) { clearTimeout(cellPopoverCloseTimer.current); cellPopoverCloseTimer.current = null; }
    if (barDrag.current || isPanning.current) return;
    const rect = cellEl.getBoundingClientRect();
    const POP_W = 230, POP_H = 130, GAP = 10;
    let x = rect.left + rect.width / 2 - POP_W / 2;
    x = Math.max(8, Math.min(x, window.innerWidth - POP_W - 8));
    const above = rect.top > POP_H + GAP + 8;
    const y = above ? rect.top - POP_H - GAP : rect.bottom + GAP;
    setCellPopover({ task, dateStr, x, y, above });
  }
  function scheduleShowCellPopover(task, dateStr, cellEl) {
    if (cellPopoverTimer.current) { clearTimeout(cellPopoverTimer.current); cellPopoverTimer.current = null; }
    cellPopoverTimer.current = setTimeout(() => {
      cellPopoverTimer.current = null;
      showCellPopoverNow(task, dateStr, cellEl);
    }, 1500);
  }
  function cancelPopoverTimer() {
    if (cellPopoverTimer.current) { clearTimeout(cellPopoverTimer.current); cellPopoverTimer.current = null; }
  }
  // Close popover on mouse leave from cell, with a short grace so the pointer
  // can travel across the 10px gap into the popover itself.
  function schedulePopoverClose() {
    if (cellPopoverCloseTimer.current) clearTimeout(cellPopoverCloseTimer.current);
    cellPopoverCloseTimer.current = setTimeout(() => {
      cellPopoverCloseTimer.current = null;
      setCellPopover(null);
    }, 140);
  }
  function cancelPopoverClose() {
    if (cellPopoverCloseTimer.current) { clearTimeout(cellPopoverCloseTimer.current); cellPopoverCloseTimer.current = null; }
  }

  /* ── Mini progress modal ── */
  // eslint-disable-next-line no-unused-vars
  function openProgressModal(task) {
    setProgressModal({ task, value: task.progreso ?? 0 });
    setCellPopover(null);
  }
  async function saveProgress() {
    if (!progressModal) return;
    setProgressSaving(true);
    const { task, value } = progressModal;
    // Optimistic update so bar reflects immediately
    setTasks(prev => prev.map(t => t.id === task.id ? { ...t, progreso: value } : t));
    const { error } = await supabase.from('tareas').update({ progreso: value }).eq('id', task.id);
    if (error) {
      await fetchTasks(); // revert by pulling DB truth
      setProgressSaving(false);
      showToast('Error al guardar: ' + error.message, 'error');
      return;
    }
    await fetchTasks();
    setProgressSaving(false);
    setProgressModal(null);
    showToast(`Progreso actualizado a ${value}%`);
  }

  /* ── Journal ── */
  // mode: all notes for a task (dateStr omitted) OR notes for task+date
  async function openJournalModal(task, dateStr) {
    const initial = dateStr
      ? (journalByDay[`${task.id}|${dateStr}`] || [])
      : (journalMap[task.id] || []);
    setJournalEntries(initial);
    setJournalText(''); setJournalAutor('Ximena Li');
    setJournalModal({ task, dateStr });

    let q = supabase.from('journal').select('*').eq('tarea_id', task.id);
    if (dateStr) q = q.eq('fecha', dateStr);
    const { data, error } = await q.order('created_at', { ascending: false });
    console.log('[journal] load', { taskId: task.id, dateStr, error, count: data?.length });
    if (error) { showToast('Error cargando: ' + error.message, 'error'); return; }
    if (data) setJournalEntries(data);
  }

  async function saveJournal() {
    if (!journalText.trim() || !journalModal) return;
    setJournalSaving(true);
    const { task, dateStr } = journalModal;
    const fechaUsed = dateStr || fmtDate(new Date());
    const payload = {
      tarea_id: task.id,
      proyecto_id: task.proyecto_id,
      fecha: fechaUsed,
      nota: journalText.trim(),
      autor: journalAutor.trim() || 'Ximena Li',
    };
    console.log('[journal] insert payload:', payload);
    const { error, data } = await supabase.from('journal').insert([payload]).select();
    console.log('[journal] insert response:', { error, data });
    setJournalSaving(false);
    if (error) { showToast('Error al guardar: ' + error.message, 'error'); return; }
    if (data && data.length > 0) {
      setJournalReady(true);
      setJournalEntries(prev => [...data, ...prev]);
      setJournalText('');
      setJournalMap(prev => ({ ...prev, [task.id]: [...data, ...(prev[task.id] || [])] }));
      setTaskNoteCounts(prev => ({ ...prev, [task.id]: (prev[task.id] || 0) + data.length }));
      setJournalByDay(prev => {
        const k = `${task.id}|${fechaUsed}`;
        return { ...prev, [k]: [...data, ...(prev[k] || [])] };
      });
      setAllJournals(prev => [...data, ...prev]);
      showToast('Nota guardada correctamente');
    }
  }

  /* ── New task ── */
  function openTaskModal() {
    fetchTeam();
    setTaskForm({ ...EMPTY_TASK_FORM,
      proyecto_id: selectedProject !== 'all' ? selectedProject : (projects[0]?.id || ''),
      categoria: activeCategories[0] || ORDERED_KEYS[0],
      tipo: ganttType || 'obra',
    });
    setShowTaskModal(true);
  }
  function handleTaskChange(e) {
    const { name, value, type, checked } = e.target;
    setTaskForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (name === 'progreso' ? Number(value) : value),
    }));
  }
  /* ── Templates ── */
  const TEMPLATE_DISENO = [
    { nombre: 'Reunión 01 — Levantamiento',     categoria: 'Reunión 01' },
    { nombre: 'Reunión 02 — Distribución',      categoria: 'Reunión 02' },
    { nombre: 'Reunión 03 — Propuesta de diseño', categoria: 'Reunión 03' },
    { nombre: 'Reunión 04 — Cambios de diseño', categoria: 'Reunión 04' },
    { nombre: 'Reunión 05 — Aprobación final',  categoria: 'Reunión 05' },
    { nombre: 'Expediente técnico',             categoria: 'Reunión 06' },
    { nombre: 'Cotización',                     categoria: 'Cotización' },
  ];
  const TEMPLATE_OBRA = [
    { nombre: 'Partida Eléctrica', categoria: 'Eléctrico' },
    { nombre: 'Drywall',           categoria: 'Drywall' },
    { nombre: 'Microcemento',      categoria: 'Microcemento' },
    { nombre: 'Carpintería',       categoria: 'Carpintería' },
    { nombre: 'Pintura',           categoria: 'Pintura' },
    { nombre: 'Implementación',    categoria: 'Implementación' },
  ];
  const [templateLoading, setTemplateLoading] = useState(false);
  const [templateDeleting, setTemplateDeleting] = useState(false);
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  // Session-only dismissal — clearing on project/ganttType switch so each
  // cronograma starts fresh and dismissing is never permanent. The toolbar
  // "Cargar plantilla" button still lets the user load it after dismissing.
  const [templateDismissedScope, setTemplateDismissedScope] = useState(null);
  const templateScope = (selectedProject && selectedProject !== 'all' && ganttType)
    ? `${selectedProject}_${ganttType}`
    : null;
  const templateDismissed = templateScope != null && templateDismissedScope === templateScope;
  function dismissTemplate() {
    if (!templateScope) return;
    setTemplateDismissedScope(templateScope);
  }
  // Reset the dismissal whenever the user navigates to a different project
  // or ganttType so the banner re-arms in the new context.
  useEffect(() => {
    setTemplateDismissedScope(null);
  }, [selectedProject, ganttType]);
  const isTypedGantt = ganttType === 'diseño' || ganttType === 'obra';
  const hasProjectSelected = selectedProject && selectedProject !== 'all';
  const showTemplateBanner = (
    !loading
    && !templateLoading
    && !templateDismissed
    && tasks.length === 0
    && hasProjectSelected
    && isTypedGantt
  );
  // "Cargar plantilla" toolbar button is always available when the user is
  // on a typed gantt with zero tasks — even after dismissing the banner.
  const canLoadTemplate = (
    !loading
    && !templateLoading
    && tasks.length === 0
    && hasProjectSelected
    && isTypedGantt
  );
  // "Eliminar plantilla" toolbar button — only when there ARE tasks so the
  // user can vacate the cronograma in one click and the banner reappears.
  const canDeleteTemplate = (
    !loading
    && hasProjectSelected
    && isTypedGantt
    && tasks.length > 0
  );
  async function loadTemplate() {
    if (templateLoading) return;
    if (!selectedProject || selectedProject === 'all') return;
    if (ganttType !== 'diseño' && ganttType !== 'obra') return;
    setTemplateLoading(true);
    const template = ganttType === 'diseño' ? TEMPLATE_DISENO : TEMPLATE_OBRA;
    const spanDays = ganttType === 'diseño' ? 7 : 30;
    const today = new Date();
    const inicio = fmtDate(today);
    const finDate = new Date(today); finDate.setDate(finDate.getDate() + spanDays);
    const fin = fmtDate(finDate);
    const rows = template.map(t => ({
      nombre: t.nombre,
      categoria: t.categoria,
      progreso: 0,
      tipo: ganttType,
      inicio,
      fin,
      proyecto_id: selectedProject,
    }));
    const { error } = await supabase.from('tareas').insert(rows);
    setTemplateLoading(false);
    if (error) { showToast('Error al cargar plantilla: ' + error.message, 'error'); return; }
    // No need to dismiss explicitly — once tasks are loaded the banner hides
    // because the `tasks.length === 0` gate is no longer satisfied.
    await refetchTasksForProject(selectedProject);
    showToast(`${rows.length} tareas cargadas de la plantilla`);
  }
  function requestDeleteAll() {
    if (templateDeleting) return;
    if (!selectedProject || selectedProject === 'all') return;
    if (ganttType !== 'diseño' && ganttType !== 'obra') return;
    setShowDeleteAllModal(true);
  }
  async function confirmDeleteAll() {
    if (templateDeleting) return;
    if (!selectedProject || selectedProject === 'all') return;
    if (ganttType !== 'diseño' && ganttType !== 'obra') return;
    setTemplateDeleting(true);
    const { error } = await supabase
      .from('tareas')
      .delete()
      .eq('proyecto_id', selectedProject)
      .eq('tipo', ganttType);
    setTemplateDeleting(false);
    setShowDeleteAllModal(false);
    if (error) { showToast('Error al eliminar: ' + error.message, 'error'); return; }
    // Re-arm the template banner — clearing the session-only dismissal so
    // the banner reappears once tasks are gone.
    setTemplateDismissedScope(null);
    await refetchTasksForProject(selectedProject);
    showToast('Cronograma vaciado');
  }

  /* ── Subtasks ── */
  function openSubtaskModal(parent) {
    setSubtaskParent(parent);
    setSubtaskForm({
      nombre: '',
      asignado: parent?.asignado || '',
      inicio: parent?.inicio || '',
      fin: parent?.fin || '',
    });
  }
  function closeSubtaskModal() {
    if (subtaskSaving) return;
    setSubtaskParent(null);
    setSubtaskForm({ nombre: '', asignado: '', inicio: '', fin: '' });
  }
  async function saveSubtask(e) {
    e.preventDefault();
    if (!subtaskParent || subtaskSaving) return;
    if (!subtaskForm.nombre.trim()) { showToast('Nombre requerido', 'error'); return; }
    setSubtaskSaving(true);
    const payload = {
      nombre: subtaskForm.nombre.trim(),
      asignado: subtaskForm.asignado || null,
      inicio: subtaskForm.inicio || null,
      fin: subtaskForm.fin || null,
      progreso: 0,
      proyecto_id: subtaskParent.proyecto_id,
      categoria: subtaskParent.categoria,
      tipo: subtaskParent.tipo || ganttType || 'obra',
      parent_id: subtaskParent.id,
      es_subtarea: true,
    };
    const { data, error } = await supabase.from('tareas').insert([payload]).select();
    setSubtaskSaving(false);
    if (error) { showToast('Error: ' + error.message, 'error'); return; }
    if (!data || data.length === 0) {
      showToast('No se insertó (RLS bloqueó el INSERT en tareas)', 'error');
      return;
    }
    // Auto-expand the parent so the new subtask is visible.
    setExpandedParents(prev => {
      const next = new Set(prev); next.add(subtaskParent.id); return next;
    });
    await refetchTasksForProject(subtaskParent.proyecto_id);
    await recomputeParentProgress(subtaskParent.id);
    closeSubtaskModal();
    showToast('Subtarea agregada');
  }
  async function toggleSubtaskDone(subtask) {
    const next = (subtask.progreso || 0) >= 100 ? 0 : 100;
    setTasks(prev => prev.map(t => t.id === subtask.id ? { ...t, progreso: next } : t));
    const { error } = await supabase.from('tareas').update({ progreso: next }).eq('id', subtask.id);
    if (error) {
      showToast('Error: ' + error.message, 'error');
      await fetchTasks();
      return;
    }
    if (subtask.parent_id) {
      await recomputeParentProgress(subtask.parent_id);
    }
  }
  async function deleteSubtask(subtask) {
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!window.confirm(`¿Eliminar la subtarea "${subtask.nombre}"?`)) return;
    setTasks(prev => prev.filter(t => t.id !== subtask.id));
    const { error } = await supabase.from('tareas').delete().eq('id', subtask.id);
    if (error) { showToast('Error: ' + error.message, 'error'); await fetchTasks(); return; }
    if (subtask.parent_id) await recomputeParentProgress(subtask.parent_id);
  }
  // Recompute progreso for a parent based on its current subtasks. Reads
  // fresh state via setTasks so a just-flipped subtask is included.
  async function recomputeParentProgress(parentId) {
    if (!parentId) return;
    let nextProgress = 0;
    let parent;
    setTasks(prev => {
      parent = prev.find(t => t.id === parentId);
      const subs = prev.filter(t => t.parent_id === parentId);
      if (subs.length === 0) {
        nextProgress = parent?.progreso ?? 0;
        return prev;
      }
      const done = subs.filter(s => (s.progreso || 0) >= 100).length;
      nextProgress = Math.round((done / subs.length) * 100);
      return prev.map(t => t.id === parentId ? { ...t, progreso: nextProgress } : t);
    });
    if (!parent) return;
    if ((parent.progreso ?? 0) === nextProgress) return;
    const { error } = await supabase.from('tareas').update({ progreso: nextProgress }).eq('id', parentId);
    if (error) {
      // eslint-disable-next-line no-console
      console.warn('[tareas] failed to persist parent progress', parentId, error.message);
    }
  }

  async function handleTaskSubmit(e) {
    e.preventDefault();
    if (taskSaving) return;
    setTaskSaving(true);
    const p = { ...taskForm };
    if (!p.inicio) p.inicio = null;
    if (!p.fin) p.fin = null;
    // Force tipo to the current gantt view so a new task can never leak
    // into the other cronograma. Default to 'obra' when ganttType is null
    // (general Gantt) — matches the column default.
    p.tipo = ganttType || 'obra';
    const { data, error } = await supabase.from('tareas').insert([p]).select();
    if (error) {
      setTaskSaving(false);
      showToast('Error: ' + error.message, 'error');
      return;
    }
    if (!data || data.length === 0) {
      setTaskSaving(false);
      showToast('No se insertó (RLS bloqueó el INSERT en tareas). Revisa las políticas.', 'error');
      return;
    }
    console.log('[tareas] inserted', data[0]?.id, '→ refetching for project', p.proyecto_id);
    await refetchTasksForProject(p.proyecto_id);
    setTaskSaving(false);
    setShowTaskModal(false);
    showToast('Tarea agregada correctamente');
  }

  /* ── Edit task ── */
  function openEditModal(task) {
    fetchTeam();
    setEditTask(task);
    setEditForm({
      nombre: task.nombre || '',
      asignado: task.asignado || '',
      categoria: task.categoria || '',
      estado: task.estado || (task.progreso === 100 ? 'Completado' : task.progreso > 0 ? 'En progreso' : 'No iniciado'),
      notas: task.notas || '',
      inicio: task.inicio || '',
      fin: task.fin || '',
      progreso: task.progreso ?? 0,
      trabaja_sabados: !!task.trabaja_sabados,
      trabaja_domingos: !!task.trabaja_domingos,
    });
  }
  function handleEditChange(e) {
    const { name, value, type, checked } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (name === 'progreso' ? Number(value) : value),
    }));
  }
  async function handleEditSubmit(e) {
    e.preventDefault();
    if (editSaving) return;
    setEditSaving(true);
    const taskId = editTask.id;
    const projectId = editTask.proyecto_id;
    const payload = {
      nombre: editForm.nombre,
      asignado: editForm.asignado,
      categoria: editForm.categoria,
      estado: editForm.estado,
      notas: editForm.notas || null,
      inicio: editForm.inicio || null,
      fin: editForm.fin || null,
      progreso: editForm.progreso,
      trabaja_sabados: !!editForm.trabaja_sabados,
      trabaja_domingos: !!editForm.trabaja_domingos,
    };
    let resp = await supabase.from('tareas').update(payload).eq('id', taskId).select();
    let migrationWarning = false;
    if (resp.error && resp.error.message && /column .* does not exist/i.test(resp.error.message)) {
      // estado/notas columns may be missing — retry without them
      const retryPayload = { nombre: payload.nombre, asignado: payload.asignado, categoria: payload.categoria, inicio: payload.inicio, fin: payload.fin, progreso: payload.progreso };
      resp = await supabase.from('tareas').update(retryPayload).eq('id', taskId).select();
      migrationWarning = !resp.error;
    }
    if (resp.error) {
      setEditSaving(false);
      showToast('Error: ' + resp.error.message, 'error');
      return;
    }
    if (!resp.data || resp.data.length === 0) {
      setEditSaving(false);
      showToast('No se actualizó (RLS bloqueó el UPDATE en tareas). Revisa las políticas.', 'error');
      return;
    }
    console.log('[tareas] updated', taskId, '→ refetching for project', projectId);
    await refetchTasksForProject(projectId);
    setEditSaving(false);
    setEditTask(null);
    showToast(migrationWarning
      ? 'Guardado (ejecuta supabase-tareas-extra-migration.sql para estado/notas)'
      : 'Tarea actualizada',
      migrationWarning ? 'error' : 'success');
  }

  async function handleDeleteTask() {
    if (!editTask || editSaving) return;
    if (!window.confirm('¿Eliminar esta tarea? Esta acción no se puede deshacer.')) return;
    setEditSaving(true);
    const taskId = editTask.id;
    const projectId = editTask.proyecto_id;
    const { data, error } = await supabase.from('tareas').delete().eq('id', taskId).select();
    if (error) {
      setEditSaving(false);
      showToast('Error al eliminar: ' + error.message, 'error');
      return;
    }
    if (!data || data.length === 0) {
      // RLS is silently no-opping the DELETE — surface it instead of showing success.
      setEditSaving(false);
      console.warn('[tareas] delete returned zero rows for', taskId, '— likely RLS blocking');
      showToast('No se eliminó. La política de RLS en la tabla tareas está bloqueando DELETE. Contacta al administrador.', 'error');
      return;
    }
    console.log('[tareas] deleted', taskId, '→ refetching for project', projectId);
    await refetchTasksForProject(projectId);
    setEditSaving(false);
    setEditTask(null);
    showToast('Tarea eliminada correctamente');
  }

  // Explicit project-scoped refetch used after any tareas CRUD. Falls back
  // to the broad fetchTasks() when the caller doesn't know the project.
  async function refetchTasksForProject(projectId) {
    if (!projectId) { await fetchTasks(); return; }
    let q = supabase
      .from('tareas')
      .select('*, proyectos(nombre)')
      .eq('proyecto_id', projectId)
      .order('inicio', { nullsFirst: false });
    if (ganttType) q = q.eq('tipo', ganttType);
    const { data, error } = await q;
    if (error) {
      console.error('[tareas] refetch error:', error);
      // Fall back to the broader fetch so state isn't left stale.
      await fetchTasks();
      return;
    }
    // If user has a specific project selected that matches, replace tasks
    // with the scoped result. If they're viewing "all", merge fresh rows
    // for this project with the other projects' rows.
    if (selectedProject === projectId || selectedProject !== 'all') {
      setTasks(data || []);
    } else {
      setTasks(prev => {
        const others = prev.filter(t => t.proyecto_id !== projectId);
        return [...others, ...(data || [])];
      });
    }
  }

  /* ── Compact (< 1024px) helpers: narrow task column + tap action sheet ── */
  function openTaskSheet(task, dateStr) {
    setCellPopover(null);
    setTaskSheet({ task, dateStr: dateStr || null });
  }

  // Returns a click handler (kept out of JSX ternaries to avoid parser ambiguity).
  function tapToSheet(task, dateStr) {
    return (e) => { e.stopPropagation(); openTaskSheet(task, dateStr); };
  }

  function resolveAssignee(task) {
    const canonical = task.asignado && (
      teamMembers.find(m => m.nombre === task.asignado) ||
      teamMembers.find(m => task.asignado && m.nombre.split(' ')[0] === task.asignado.split(' ')[0])
    );
    return canonical ? canonical.nombre : task.asignado;
  }

  function renderCompactLeftPanel() {
    const nameW = isMobile ? 140 : 200;
    const progW = isMobile ? 50 : 64;
    const leftW = nameW + progW;
    return (
      <div style={{ ...Z.leftPanel, width: leftW, minWidth: leftW, ...Z.leftPanelCompact }}>
        <div style={Z.leftHeaderRow}>
          <div style={{ ...Z.leftHeaderCell, width: nameW }}>ACTIVIDADES</div>
          <div style={{ ...Z.leftHeaderCell, width: progW, justifyContent:'center', padding:0, borderRight:'none' }}>%</div>
        </div>
        {activeCategories.map((cat, ci) => (
          <React.Fragment key={cat}>
            <div style={{ ...Z.catHeader, background: CAT_HDR_ALT[ci % CAT_HDR_ALT.length], ...Z.catHeaderCompact }}>
              {cat.toUpperCase()}
            </div>
            {rowsForCategory(cat).map(row => {
              const task = row.task;
              if (row.isSubtask) {
                const checked = (task.progreso || 0) >= 100;
                return (
                  <div
                    key={task.id}
                    data-task-row={task.id}
                    data-subtask="1"
                    role="button"
                    tabIndex={0}
                    style={{ ...Z.subtaskRow, cursor:'pointer' }}
                    onClick={() => openTaskSheet(task, null)}
                  >
                    <div style={{ ...Z.dataCell, width: nameW, gap:6, paddingLeft:22, fontSize:13 }}>
                      <span style={{ flexShrink:0, fontSize:12 }}>{checked ? '✅' : '☐'}</span>
                      <span style={{
                        overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', flex:1, minWidth:0,
                        color: checked ? 'var(--text-light)' : 'var(--text-primary)',
                        textDecoration: checked ? 'line-through' : 'none',
                      }}>{task.nombre}</span>
                    </div>
                    <div style={{ ...Z.dataCell, width: progW, justifyContent:'center', padding:0, borderRight:'none', fontSize:12, color:'var(--text-light)' }}>
                      {checked ? '✓' : '—'}
                    </div>
                  </div>
                );
              }
              const subCount = (subtasksByParent[task.id] || []).length;
              const hasSubs = subCount > 0;
              const expanded = isExpanded(task.id);
              const progValue = hasSubs ? effectiveProgress(task) : (task.progreso ?? 0);
              const pill = getProgressPillStyle(progValue);
              return (
                <div
                  key={task.id}
                  data-task-row={task.id}
                  role="button"
                  tabIndex={0}
                  style={{ ...Z.dataRow, cursor:'pointer' }}
                  onClick={() => openTaskSheet(task, null)}
                >
                  <div style={{ ...Z.dataCell, width: nameW, padding:'0 6px 0 0', gap:2, fontSize:13, fontWeight: hasSubs ? 700 : 500 }}>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); if (hasSubs) toggleExpanded(task.id); }}
                      style={{ ...Z.expandArrow, ...Z.expandArrowCompact, visibility: hasSubs ? 'visible' : 'hidden' }}
                      aria-label={expanded ? 'Colapsar subtareas' : 'Expandir subtareas'}
                    >
                      {expanded ? '▼' : '▶'}
                    </button>
                    <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', flex:1, minWidth:0 }}>{task.nombre}</span>
                  </div>
                  <div style={{ ...Z.dataCell, width: progW, justifyContent:'center', padding:0, borderRight:'none' }}>
                    <span style={{ ...Z.progPillCompact, background: pill.bg, color: pill.color }}>{getProgressLabel(progValue)}</span>
                  </div>
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>
    );
  }

  function renderTaskSheet() {
    const t = taskSheet ? (tasks.find(x => x.id === taskSheet.task.id) || taskSheet.task) : null;
    const dateStr = taskSheet?.dateStr || null;
    let body = null;
    if (t) {
      const isSub = !!(t.parent_id || t.es_subtarea);
      const hasSubs = !isSub && (subtasksByParent[t.id] || []).length > 0;
      const stats = hasSubs ? subtaskStats(t.id) : null;
      const progValue = hasSubs ? effectiveProgress(t) : (t.progreso ?? 0);
      const durDays = daysBetween(t.inicio, t.fin, !!t.trabaja_sabados, !!t.trabaja_domingos);
      const noteCount = Math.max(taskNoteCounts[t.id] || 0, (journalMap[t.id] || []).length);
      const done = (t.progreso || 0) >= 100;
      const details = [
        ['Asignado', resolveAssignee(t) || '—'],
        ['Categoría', t.categoria || '—'],
        ['Inicio', fmtExcel(t.inicio)],
        ['Fin', fmtExcel(t.fin)],
        ['Días', durDays || '—'],
        ['Progreso', `${progValue}%${hasSubs ? ` · ${stats.done}/${stats.total} subtareas` : ''}`],
        ['Notas', noteCount],
      ];
      const go = (fn) => () => { setTaskSheet(null); fn(); };
      body = (
        <>
          {dateStr && (
            <div style={Z.sheetDate}>
              {toLocal(dateStr).toLocaleDateString('es-MX', { weekday:'long', day:'numeric', month:'long', year:'numeric' })}
            </div>
          )}
          <div style={Z.sheetDetails}>
            {details.map(([label, value]) => (
              <div key={label} style={Z.sheetDetailRow}>
                <span style={Z.sheetDetailLabel}>{label}</span>
                <span style={Z.sheetDetailValue}>{value}</span>
              </div>
            ))}
          </div>
          <button type="button" style={{ ...Z.sheetActionBtn, ...Z.sheetActionPrimary }} onClick={go(() => openJournalModal(t, dateStr || undefined))}>
            Añadir nota al journal
          </button>
          <button type="button" style={Z.sheetActionBtn} onClick={go(() => openEditModal(t))}>
            Editar tarea
          </button>
          {!isSub && !hasSubs && (
            <button type="button" style={Z.sheetActionBtn} onClick={go(() => openProgressModal(t))}>
              Editar progreso
            </button>
          )}
          {hasSubs && (
            <button type="button" style={Z.sheetActionBtn} onClick={go(() => toggleExpanded(t.id))}>
              {isExpanded(t.id) ? 'Ocultar subtareas' : 'Ver subtareas'}
            </button>
          )}
          {!isSub && (
            <button type="button" style={Z.sheetActionBtn} onClick={go(() => openSubtaskModal(t))}>
              Añadir subtarea
            </button>
          )}
          {isSub && (
            <button type="button" style={Z.sheetActionBtn} onClick={go(() => toggleSubtaskDone(t))}>
              {done ? 'Desmarcar subtarea' : 'Marcar como completada'}
            </button>
          )}
          {isSub && (
            <button type="button" style={{ ...Z.sheetActionBtn, ...Z.sheetActionDanger }} onClick={go(() => deleteSubtask(t))}>
              Eliminar subtarea
            </button>
          )}
        </>
      );
    }
    return (
      <BottomSheet open={!!t} onClose={closeTaskSheet} title={t ? t.nombre : ''}>
        {body}
      </BottomSheet>
    );
  }

  function assigneeMember(task) {
    return task.asignado && (
      teamMembers.find(m => m.nombre === task.asignado) ||
      teamMembers.find(m => task.asignado && m.nombre.split(' ')[0] === task.asignado.split(' ')[0])
    );
  }

  function renderCompactViewBar() {
    const seg = (active) => (active ? { ...Z.segBtn, ...Z.segBtnActive } : Z.segBtn);
    return (
      <div style={Z.compactBar} data-export-hide>
        <div style={Z.segGroup} role="tablist" aria-label="Vista">
          <button type="button" role="tab" aria-selected={compactView === 'cronograma'} style={seg(compactView === 'cronograma')} onClick={() => setCompactView('cronograma')}>Cronograma</button>
          <button type="button" role="tab" aria-selected={compactView === 'lista'} style={seg(compactView === 'lista')} onClick={() => setCompactView('lista')}>Lista</button>
        </div>
        {compactView === 'cronograma' && (
          <>
            <div style={Z.compactNavRow}>
              <button type="button" style={Z.compactNavBtn} onClick={() => compactStepWeek(-1)} aria-label="Semana anterior">◀</button>
              <span style={Z.compactRange} aria-live="polite">{compactRange || ' '}</span>
              <button type="button" style={Z.compactNavBtn} onClick={() => compactStepWeek(1)} aria-label="Semana siguiente">▶</button>
            </div>
            <div style={Z.compactNavRow}>
              <button type="button" style={Z.compactTodayBtn} onClick={compactScrollToday}>Hoy</button>
              <div style={{ ...Z.segGroup, flex:1 }} role="group" aria-label="Zoom">
                <button type="button" aria-pressed={compactZoomMode === 'semana'} style={seg(compactZoomMode === 'semana')} onClick={() => setCompactZoomMode('semana')}>Semana</button>
                <button type="button" aria-pressed={compactZoomMode === 'mes'} style={seg(compactZoomMode === 'mes')} onClick={() => setCompactZoomMode('mes')}>Mes</button>
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  function renderCompactList() {
    if (loading) return <div style={Z.card}><div style={Z.loadingMsg}>Cargando...</div></div>;
    if (filteredTasks.length === 0) {
      return (
        <div style={Z.card}>
          <div style={Z.loadingMsg}>{searchQuery.trim() ? 'No hay tareas que coincidan con la búsqueda.' : `No hay tareas ${ganttType ? `de ${ganttType}` : ''} para este proyecto.`}</div>
        </div>
      );
    }
    return (
      <div style={Z.listWrap}>
        {activeCategories.map((cat, ci) => (
          <section key={cat} style={Z.listSection}>
            <div style={{ ...Z.listCatHeader, background: CAT_HDR_ALT[ci % CAT_HDR_ALT.length] }}>{cat}</div>
            {rowsForCategory(cat).map(row => {
              const t = row.task;
              const isSub = row.isSubtask;
              const subTotal = isSub ? 0 : (subtasksByParent[t.id] || []).length;
              const hasSubs = subTotal > 0;
              const expanded = hasSubs && isExpanded(t.id);
              const prog = Math.max(0, Math.min(100, hasSubs ? effectiveProgress(t) : (t.progreso ?? 0)));
              const durDays = daysBetween(t.inicio, t.fin, !!t.trabaja_sabados, !!t.trabaja_domingos) || 0;
              const overdue = !!t.fin && t.fin < todayStr && prog < 100;
              const dot = prog >= 100 ? '#4A7C59' : overdue ? CLR_RED : prog > 0 ? '#C9A96E' : '#C4BDB3';
              const dotLabel = prog >= 100 ? 'Completada' : overdue ? 'Atrasada' : prog > 0 ? 'En progreso' : 'No iniciada';
              const member = assigneeMember(t);
              const who = member ? member.nombre : t.asignado;
              return (
                <div
                  key={t.id}
                  data-task-row={t.id}
                  role="button"
                  tabIndex={0}
                  style={isSub ? { ...Z.listCard, ...Z.listCardSub } : Z.listCard}
                  onClick={() => openTaskSheet(t, null)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openTaskSheet(t, null); } }}
                >
                  <div style={Z.listCardTop}>
                    <span style={{ ...Z.listDot, background: dot }} title={dotLabel} aria-label={dotLabel} />
                    <span style={{ ...Z.listName, fontWeight: hasSubs ? 700 : 600 }}>{t.nombre}</span>
                    {hasSubs && (
                      <button
                        type="button"
                        style={Z.listChevron}
                        onClick={(e) => { e.stopPropagation(); toggleExpanded(t.id); }}
                        aria-expanded={expanded}
                        aria-label={expanded ? 'Ocultar subtareas' : 'Ver subtareas'}
                      >
                        {expanded ? '▾' : '▸'}
                      </button>
                    )}
                  </div>
                  <div style={Z.listMetaRow}>
                    <span style={{ ...Z.listAvatar, background: member?.color || '#9B9B9B' }}>{getInitials(who)}</span>
                    <span style={Z.listWho}>{who || 'Sin asignar'}</span>
                  </div>
                  <div style={Z.listDates}>
                    {fmtDayMonth(t.inicio)} → {fmtDayMonth(t.fin)} · {durDays} día{durDays === 1 ? '' : 's'}
                  </div>
                  <div style={Z.listProgressRow}>
                    <div style={Z.listBar}>
                      <div style={{ ...Z.listBarFill, width: `${prog}%`, background: prog >= 100 ? '#4A7C59' : BAR_WINE }} />
                    </div>
                    <span style={Z.listPct}>{prog}%{hasSubs ? ` · ${subtaskStats(t.id).done}/${subTotal}` : ''}</span>
                  </div>
                </div>
              );
            })}
          </section>
        ))}
      </div>
    );
  }

  /* ── Height for today marker ── */
  let totalH = 0;
  for (const cat of activeCategories) totalH += CAT_H + rowsForCategory(cat).length * ROW_H;

  /* ================================================================ */
  return (
    <div style={isMobile ? { ...Z.container, ...pageMobile } : Z.container}>
      <style>{`
        .gantt-scroll::-webkit-scrollbar{display:none}
        .gantt-scroll{scrollbar-width:none;-ms-overflow-style:none}
        @keyframes mqm-spin { to { transform: rotate(360deg); } }
        .gantt-export-mode { color-adjust: exact; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .gantt-export-mode * { color-adjust: exact !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        .gantt-export-mode [data-export-hide] { display: none !important; }
        .gantt-export-mode textarea { color: #1a1a1a !important; }
      `}</style>

      {/* Back bar */}
      {onBack && (
        <div style={isMobile ? { ...Z.backBar, ...Z.backBarMobile } : Z.backBar}>
          <button style={isMobile ? { ...Z.backBtn, ...Z.tapCompact } : Z.backBtn} onClick={onBack}>
            <span style={{ fontSize:14, lineHeight:1 }}>←</span>
            <span>Volver a proyectos</span>
          </button>
          {ganttType && (
            <span style={{ ...Z.typePill, ...(ganttType === 'diseño' ? Z.typePillDiseno : Z.typePillObra) }}>
              {ganttType === 'diseño' ? '📐 Cronograma de Diseño' : '🏗️ Cronograma de Obra'}
            </span>
          )}
        </div>
      )}

      {/* ─── EXCEL-STYLE HEADER ─── */}
      <div ref={exportRef}>
      <div style={isMobile ? { ...Z.excelHeader, ...Z.excelHeaderMobile } : Z.excelHeader}>
        <div style={isMobile ? { ...Z.excelHeaderTop, ...Z.excelHeaderTopMobile } : Z.excelHeaderTop}>
          <div style={Z.hdrLeft}>
            <h1 style={isMobile ? { ...Z.hdrTitle, ...Z.hdrTitleMobile } : Z.hdrTitle}>GANTT: {ganttType === 'diseño' ? 'CRONOGRAMA DE DISEÑO' : 'CRONOGRAMA DE OBRA'}</h1>
            <div style={isMobile ? { ...Z.hdrRow, ...Z.hdrRowMobile } : Z.hdrRow}>
              <span style={isMobile ? { ...Z.hdrLabel, ...Z.hdrLabelMobile } : Z.hdrLabel}>Nombre de Proyecto</span>
              <span style={isMobile ? { ...Z.hdrValue, ...Z.hdrValueMobile } : Z.hdrValue}>{projectName}</span>
            </div>
            <div style={isMobile ? { ...Z.hdrRow, ...Z.hdrRowMobile } : Z.hdrRow}>
              <span style={isMobile ? { ...Z.hdrLabel, ...Z.hdrLabelMobile } : Z.hdrLabel}>Arquitecta del proyecto</span>
              <span style={isMobile ? { ...Z.hdrValue, ...Z.hdrValueMobile } : Z.hdrValue}>{architect}</span>
            </div>
            {durationSummary.earliest && durationSummary.latest && (
              <div style={Z.hdrDurationRow}>
                <span style={isMobile ? { ...Z.hdrDurationBadge, ...Z.hdrDurationMobile } : Z.hdrDurationBadge}>
                  📅 Duración total: <strong>{durationSummary.workingDays} día{durationSummary.workingDays === 1 ? '' : 's'} hábiles</strong>
                </span>
                <span style={isMobile ? { ...Z.hdrDurationMeta, ...Z.hdrDurationMobile } : Z.hdrDurationMeta}>
                  Inicio: <strong>{fmtExcel(durationSummary.earliest)}</strong>
                  {'   ·   '}
                  Fin estimado: <strong>{fmtExcel(durationSummary.latest)}</strong>
                </span>
              </div>
            )}
          </div>
          <div style={isMobile ? { ...Z.hdrRight, ...Z.hdrRightMobile } : Z.hdrRight}>
            <div style={Z.logoBlock}>
              <span style={Z.logoText}>m</span>
              <span style={Z.logoSub}>másquemuros</span>
            </div>
            <div style={isMobile ? { ...Z.hdrRowRight, ...Z.hdrRowMobile } : Z.hdrRowRight}>
              <span style={isMobile ? { ...Z.hdrLabel, ...Z.hdrLabelMobile } : Z.hdrLabel}>Inicio del proyecto</span>
              <span style={isMobile ? { ...Z.hdrValue, ...Z.hdrValueMobile } : Z.hdrValue}>{fmtExcel(projectStart)}</span>
            </div>
            <div style={isMobile ? { ...Z.hdrRowRight, ...Z.hdrRowMobile } : Z.hdrRowRight}>
              <span style={isMobile ? { ...Z.hdrLabel, ...Z.hdrLabelMobile } : Z.hdrLabel}>Semana para mostrar</span>
              <span style={isMobile ? { ...Z.hdrValue, ...Z.hdrValueMobile } : Z.hdrValue}>{weekToShow}</span>
            </div>
          </div>
        </div>

        {selectedProj && (
          <div style={Z.hdrObsWrap}>
            <div style={Z.hdrObsHeader}>
              <span style={isMobile ? { ...Z.hdrLabel, ...Z.hdrLabelMobile } : Z.hdrLabel}>Observación del proyecto</span>
              <span style={{ fontSize:11, color: observacionSavedFlash ? '#4A7C59' : 'var(--text-light)', fontWeight:600, minHeight:14 }}>
                {observacionSaving ? 'Guardando…' : observacionSavedFlash ? 'Guardado ✓' : ''}
              </span>
            </div>
            <textarea
              value={observacionDraft}
              onChange={e => setObservacionDraft(e.target.value)}
              onBlur={saveObservacion}
              placeholder="Escribe una breve descripción o nota sobre este proyecto…"
              rows={2}
              style={Z.hdrObsTextarea}
            />
          </div>
        )}
      </div>

      {/* Controls */}
      <div style={Z.controls} data-export-hide>
        <select style={isMobile ? { ...Z.projectSelect, ...Z.projectSelectMobile } : Z.projectSelect} value={selectedProject} onChange={e => setSelectedProject(e.target.value)}>
          <option value="all">Todos los proyectos</option>
          {projects.map(p => (
            <option key={p.id} value={p.id}>
              {p.nombre}{ganttType ? ` — ${ganttType === 'diseño' ? 'Diseño' : 'Obra'}` : ''}
            </option>
          ))}
        </select>
        <button
          type="button"
          style={isMobile ? { ...Z.exportPdfBtn, ...Z.tapCompact } : Z.exportPdfBtn}
          onClick={exportPDF}
          disabled={pdfExporting || loading || filteredTasks.length === 0}
          title="Exportar Gantt como PDF"
        >
          {pdfExporting ? (
            <>
              <span style={Z.exportSpinner} aria-hidden="true" />
              Generando…
            </>
          ) : (
            <>📄 Exportar PDF</>
          )}
        </button>
        <div style={isMobile ? { ...Z.searchWrap, ...Z.searchWrapMobile } : Z.searchWrap}>
          <span style={Z.searchIcon} aria-hidden="true">
            <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="9" cy="9" r="6" />
              <line x1="13.5" y1="13.5" x2="17" y2="17" />
            </svg>
          </span>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Buscar tarea o responsable..."
            style={isMobile ? { ...Z.searchInput, ...Z.searchInputMobile } : Z.searchInput}
            aria-label="Buscar tarea o responsable"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              style={isMobile ? { ...Z.searchClearBtn, ...Z.searchClearBtnMobile } : Z.searchClearBtn}
              aria-label="Limpiar búsqueda"
              title="Limpiar búsqueda"
            >
              ✕
            </button>
          )}
        </div>
        {!isCompact && (<>
        <div style={isMobile ? { display:'flex', gap:6, alignItems:'center', flexWrap:'wrap' } : { display:'flex', gap:6, alignItems:'center' }}>
          <div style={Z.zoomGroup} title="Zoom (Cmd/Ctrl + / -)">
            <button
              type="button"
              style={isMobile ? { ...Z.zoomBtn, ...Z.tapCompact } : Z.zoomBtn}
              onClick={() => adjustZoom(-2)}
              disabled={dayW <= DAY_W_MIN}
              aria-label="Reducir zoom"
            >−</button>
            <select
              value={DAY_W_PRESETS.find(p => p.px === dayW)?.key || ''}
              onChange={(e) => {
                const preset = DAY_W_PRESETS.find(p => p.key === e.target.value);
                if (preset) setDayW(preset.px);
              }}
              style={Z.zoomSelect}
              aria-label="Nivel de zoom"
            >
              {!DAY_W_PRESETS.find(p => p.px === dayW) && (
                <option value="">{dayW}px</option>
              )}
              {DAY_W_PRESETS.map(p => (
                <option key={p.key} value={p.key}>{p.label}</option>
              ))}
            </select>
            <button
              type="button"
              style={isMobile ? { ...Z.zoomBtn, ...Z.tapCompact } : Z.zoomBtn}
              onClick={() => adjustZoom(2)}
              disabled={dayW >= DAY_W_MAX}
              aria-label="Aumentar zoom"
            >+</button>
          </div>
          <button style={isMobile ? { ...Z.navBtn, ...Z.tapCompact } : Z.navBtn} onClick={() => jumpWeeks(-1)} title="← 4 semanas"><svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="10,3 5,8 10,13"/></svg></button>
          <button style={isMobile ? { ...Z.todayBtn, ...Z.tapCompact } : Z.todayBtn} onClick={scrollToToday}>Hoy</button>
          <button style={isMobile ? { ...Z.navBtn, ...Z.tapCompact } : Z.navBtn} onClick={() => jumpWeeks(1)} title="→ 4 semanas"><svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="6,3 11,8 6,13"/></svg></button>
        </div>
        <span style={isMobile ? { ...Z.navRange, ...Z.navRangeMobile } : Z.navRange}>{visibleRange}</span>
        </>)}
        <button style={isMobile ? { ...Z.addBtn, ...Z.tapCompact } : Z.addBtn} onClick={openTaskModal}>+ Nueva Tarea</button>
        {canLoadTemplate && (
          <button
            type="button"
            style={isMobile ? { ...Z.templateLoadBtn, ...Z.tapCompact } : Z.templateLoadBtn}
            onClick={loadTemplate}
            disabled={templateLoading}
            title="Cargar la plantilla de tareas para este tipo de cronograma"
          >
            {templateLoading ? 'Cargando…' : '📋 Cargar plantilla'}
          </button>
        )}
        {canDeleteTemplate && (
          <button
            type="button"
            style={isMobile ? { ...Z.templateDeleteBtn, ...Z.tapCompact } : Z.templateDeleteBtn}
            onClick={requestDeleteAll}
            disabled={templateDeleting}
            title="Vaciar el cronograma — borra todas las tareas de este proyecto + tipo"
          >
            {templateDeleting ? 'Eliminando…' : '🗑️ Eliminar plantilla'}
          </button>
        )}
      </div>

      {showTemplateBanner && (
        <div style={Z.templateBanner}>
          <div style={Z.templateBannerText}>
            <strong>Este cronograma está vacío.</strong> ¿Quieres cargar una plantilla de tareas?
          </div>
          <div style={Z.templateBannerActions}>
            <button type="button" style={isMobile ? { ...Z.templateBannerSecondary, ...Z.tapCompact } : Z.templateBannerSecondary} onClick={dismissTemplate} disabled={templateLoading}>
              Empezar vacío
            </button>
            <button type="button" style={isMobile ? { ...Z.templateBannerPrimary, ...Z.tapCompact } : Z.templateBannerPrimary} onClick={loadTemplate} disabled={templateLoading}>
              {templateLoading ? 'Cargando…' : 'Cargar plantilla'}
            </button>
            <button
              type="button"
              style={isMobile ? { ...Z.templateBannerClose, ...Z.tapCompact } : Z.templateBannerClose}
              onClick={dismissTemplate}
              disabled={templateLoading}
              aria-label="Descartar banner"
              title="No volver a mostrar para este cronograma"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* ─── GANTT CARD ─── */}
      {isCompact && renderCompactViewBar()}
      {isCompact && compactView === 'lista' ? renderCompactList() : (
      <div style={Z.card}>
        {loading ? <div style={Z.loadingMsg}>Cargando...</div>
        : filteredTasks.length === 0 ? <div style={Z.loadingMsg}>{searchQuery.trim() ? 'No hay tareas que coincidan con la búsqueda.' : `No hay tareas ${ganttType ? `de ${ganttType}` : ''} para este proyecto.`}</div>
        : (
          <div style={Z.ganttWrap}>
            {/* LEFT: fixed columns */}
            {isCompact ? renderCompactLeftPanel() : (
            <div style={Z.leftPanel}>
              {/* Column headers */}
              <div style={Z.leftHeaderRow}>
                <div style={{ ...Z.leftHeaderCell, width: COL_ACT }}>ACTIVIDADES</div>
                <div style={{ ...Z.leftHeaderCell, width: COL_ASG }}>ASIGNADO A</div>
                <div style={{ ...Z.leftHeaderCell, width: COL_PROG, textAlign:'center' }}>PROGRESO</div>
                <div style={{ ...Z.leftHeaderCell, width: COL_INI, textAlign:'center' }}>INICIO</div>
                <div style={{ ...Z.leftHeaderCell, width: COL_FIN, textAlign:'center' }}>FIN</div>
                <div style={{ ...Z.leftHeaderCell, width: COL_DAYS, textAlign:'center' }}>DÍAS</div>
                <div style={{ ...Z.leftHeaderCell, width: COL_NOTE, textAlign:'center', borderRight:'none' }}>NOTA</div>
              </div>

              {/* Task rows grouped by category */}
              {activeCategories.map((cat, ci) => {
                const hdrColor = CAT_HDR_ALT[ci % CAT_HDR_ALT.length];
                return (
                  <React.Fragment key={cat}>
                    {/* Category header — full width, alternating colors */}
                    <div style={{ ...Z.catHeader, background: hdrColor }}>
                      {cat.toUpperCase()}
                    </div>
                    {rowsForCategory(cat).map(row => {
                      const task = row.task;
                      if (row.isSubtask) {
                        // Subtask row — indented, no Gantt bar (rendered as
                        // a flat row that still occupies ROW_H so the right
                        // panel can render an empty alignment row).
                        const canonical = task.asignado && (
                          teamMembers.find(m => m.nombre === task.asignado) ||
                          teamMembers.find(m => task.asignado && m.nombre.split(' ')[0] === task.asignado.split(' ')[0])
                        );
                        const displayName = canonical ? canonical.nombre : task.asignado;
                        const avatarColor = canonical?.color || '#9B9B9B';
                        const checked = (task.progreso || 0) >= 100;
                        const durDays = daysBetween(task.inicio, task.fin, !!task.trabaja_sabados, !!task.trabaja_domingos);
                        return (
                          <div
                            key={task.id}
                            data-task-row={task.id}
                            data-subtask="1"
                            style={Z.subtaskRow}
                          >
                            <div style={{ ...Z.dataCell, width: COL_ACT, gap:6, paddingLeft:24 }} title={task.nombre}>
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); toggleSubtaskDone(task); }}
                                style={Z.subtaskCheckbox}
                                title={checked ? 'Desmarcar' : 'Marcar como completada'}
                              >
                                {checked ? '✅' : '☐'}
                              </button>
                              <span style={{
                                overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
                                flex:1, minWidth:0, fontSize:12,
                                color: checked ? 'var(--text-light)' : 'var(--text-primary)',
                                textDecoration: checked ? 'line-through' : 'none',
                              }}>{task.nombre}</span>
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); deleteSubtask(task); }}
                                style={Z.subtaskDeleteBtn}
                                title="Eliminar subtarea"
                              >
                                ×
                              </button>
                            </div>
                            <div style={{ ...Z.dataCell, width: COL_ASG, color:'var(--text-secondary)', gap:6, fontSize:11.5 }} title={displayName}>
                              {displayName && (
                                <span style={{ width:16, height:16, borderRadius:'50%', background:avatarColor, color:'#fff', fontSize:7, fontWeight:700, display:'inline-flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                                  {getInitials(displayName)}
                                </span>
                              )}
                              <span style={{ overflow:'hidden', textOverflow:'ellipsis' }}>{displayName || '—'}</span>
                            </div>
                            <div style={{ ...Z.dataCell, width: COL_PROG, justifyContent:'center', fontSize:10, color: 'var(--text-light)' }}>
                              {checked ? '✓' : '—'}
                            </div>
                            {editableDateCell(task, 'inicio', { width: COL_INI, fontSize: 10.5, color: 'var(--text-light)' })}
                            {editableDateCell(task, 'fin', { width: COL_FIN, fontSize: 10.5, color: 'var(--text-light)' })}
                            <div style={{ ...Z.dataCell, width: COL_DAYS, justifyContent:'center', fontSize:10.5, color:'var(--wine)' }}>{durDays || '—'}</div>
                            <div style={{ ...Z.dataCell, width: COL_NOTE, justifyContent:'center', padding:0, borderRight:'none' }} />
                          </div>
                        );
                      }

                      // Parent / regular task row.
                      const subCount = (subtasksByParent[task.id] || []).length;
                      const hasSubs = subCount > 0;
                      const expanded = isExpanded(task.id);
                      const progValue = hasSubs ? effectiveProgress(task) : (task.progreso ?? 0);
                      const pill = getProgressPillStyle(progValue);
                      const noteCountGlobal = taskNoteCounts[task.id] || 0;
                      const noteCountLocal = (journalMap[task.id] || []).length;
                      const noteCount = Math.max(noteCountGlobal, noteCountLocal);
                      const hasNotes = noteCount > 0;
                      const durDays = daysBetween(task.inicio, task.fin, !!task.trabaja_sabados, !!task.trabaja_domingos);
                      const canonical = task.asignado && (
                        teamMembers.find(m => m.nombre === task.asignado) ||
                        teamMembers.find(m => task.asignado && m.nombre.split(' ')[0] === task.asignado.split(' ')[0])
                      );
                      const displayName = canonical ? canonical.nombre : task.asignado;
                      const avatarColor = canonical?.color || '#9B9B9B';
                      return (
                        <div
                          key={task.id}
                          data-task-row={task.id}
                          style={Z.dataRow}
                          onMouseEnter={() => setHoverParentId(task.id)}
                          onMouseLeave={() => setHoverParentId(prev => prev === task.id ? null : prev)}
                        >
                          <div style={{ ...Z.dataCell, width: COL_ACT, fontWeight: hasSubs ? 700 : 500, gap:4 }} title={task.nombre}>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); if (hasSubs) toggleExpanded(task.id); }}
                              style={{
                                ...Z.expandArrow,
                                visibility: hasSubs ? 'visible' : 'hidden',
                              }}
                              title={expanded ? 'Colapsar' : 'Expandir'}
                              aria-label={expanded ? 'Colapsar subtareas' : 'Expandir subtareas'}
                            >
                              {expanded ? '▼' : '▶'}
                            </button>
                            <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', flex:1, minWidth:0 }}>{task.nombre}</span>
                            {task.trabaja_sabados && (
                              <span style={Z.weekendBadge} title="Trabaja sábados">S</span>
                            )}
                            {task.trabaja_domingos && (
                              <span style={Z.weekendBadge} title="Trabaja domingos">D</span>
                            )}
                            {hoverParentId === task.id && (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); openSubtaskModal(task); }}
                                style={Z.addSubtaskBtn}
                                title="Añadir subtarea"
                              >
                                + Subtarea
                              </button>
                            )}
                          </div>
                          <div style={{ ...Z.dataCell, width: COL_ASG, color:'var(--text-secondary)', gap:6 }} title={displayName}>
                            {displayName && (
                              <span style={{ width:18, height:18, borderRadius:'50%', background:avatarColor, color:'#fff', fontSize:8, fontWeight:700, display:'inline-flex', alignItems:'center', justifyContent:'center', flexShrink:0, letterSpacing:'0.02em' }}>
                                {getInitials(displayName)}
                              </span>
                            )}
                            <span style={{ overflow:'hidden', textOverflow:'ellipsis' }}>{displayName || '—'}</span>
                          </div>
                          <div style={{ ...Z.dataCell, width: COL_PROG, justifyContent:'center', padding:0 }}>
                            <span style={{ background: pill.bg, color: pill.color, fontSize:10, fontWeight:700, padding:'3px 10px', borderRadius:10, minWidth:64, textAlign:'center', lineHeight:1.15, display:'inline-flex', flexDirection:'column', alignItems:'center', gap:1 }}>
                              <span style={{ fontSize:11, fontWeight:800 }}>{getProgressLabel(progValue)}</span>
                              <span style={{ fontSize:9, fontWeight:600, opacity:0.9 }}>{hasSubs ? `${subtaskStats(task.id).done}/${subCount}` : (durDays ? `${durDays}d` : '—')}</span>
                            </span>
                          </div>
                          {editableDateCell(task, 'inicio', { width: COL_INI, fontSize: 11 })}
                          {editableDateCell(task, 'fin', { width: COL_FIN, fontSize: 11 })}
                          <div style={{ ...Z.dataCell, width: COL_DAYS, justifyContent:'center', fontSize:11, fontWeight:600, color:'var(--wine)' }}>{durDays}</div>
                          <div style={{ ...Z.dataCell, width: COL_NOTE, justifyContent:'center', padding:0, borderRight:'none' }}>
                            <button onClick={() => openJournalModal(task)} title={hasNotes ? `${noteCount} nota${noteCount>1?'s':''}` : 'Agregar nota'}
                              style={{ background:'none', border:'none', cursor:'pointer', padding:3, display:'flex', alignItems:'center', gap:2 }}>
                              <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke={hasNotes ? '#C9A96E' : '#ccc'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M14 10l-4 4H3a1 1 0 01-1-1V3a1 1 0 011-1h10a1 1 0 011 1v7z"/>
                                <line x1="5" y1="6" x2="11" y2="6"/><line x1="5" y1="9" x2="9" y2="9"/>
                              </svg>
                              {hasNotes && <span style={{ fontSize:9, fontWeight:700, color:'#C9A96E' }}>{noteCount}</span>}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </div>
            )}

            {/* RIGHT: scrollable timeline */}
            <div ref={scrollRef} data-gantt-right className="gantt-scroll" style={isCompact ? { ...Z.rightPanel, ...Z.rightPanelCompact, ...Z.rightPanelSnap } : Z.rightPanel} onMouseDown={isCompact ? undefined : onPanMouseDown}>
              <div style={{ width: timelineWidth, minWidth: timelineWidth }}>
                {/* Week header row */}
                <div style={{ display:'flex', height:HDR_WEEK_H, borderBottom:'1px solid var(--border)', background:'#fff' }}>
                  {weekGroups.map((wk, wi) => {
                    const w = wk.days.length * dayW;
                    return (
                      <div key={wi} style={{
                        width: w, minWidth: w,
                        display:'flex', alignItems:'center', justifyContent:'center',
                        borderRight:'1px solid var(--border)',
                        fontSize:10, fontWeight:700, color:'var(--wine)',
                        background:'#FDFBF8',
                      }}>
                        {wk.start.date.toLocaleDateString('es-MX', { day:'numeric', month:'short' })}
                      </div>
                    );
                  })}
                </div>
                {/* Day-of-week letter + day-number row */}
                <div style={{ display:'flex', height:HDR_DOW_H, borderBottom:'1px solid var(--border)' }}>
                  {allDays.map((d, di) => {
                    const we = d.dow === 0 || d.dow === 6;
                    const fer = FERIADOS_PERU.has(d.dateStr);
                    const off = we || fer;
                    const td = d.dateStr === todayStr;
                    return (
                      <div key={di} style={{
                        width:dayW, minWidth:dayW,
                        display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
                        borderRight:'1px solid #f0ebe3',
                        fontSize:9, fontWeight: td ? 800 : 600,
                        color: td ? CLR_RED : off ? '#9B9B9B' : '#6B6B6B',
                        background: td ? '#FFF0F0' : off ? '#EDE7DF' : '#fff',
                        lineHeight: 1,
                        position: 'relative',
                      }}
                      title={fer ? `Feriado · ${fmtExcel(d.dateStr)}` : undefined}
                      >
                        {showDayLetters && (
                          <span style={{ fontSize:8, fontWeight:700, opacity:0.7 }}>{DOW_INITIAL[d.dow]}</span>
                        )}
                        <span style={{ fontSize: dayW < 14 ? 9 : 10, fontWeight: td ? 800 : 600 }}>{d.date.getDate()}</span>
                        {fer && (
                          <span style={{
                            position:'absolute', top:1, right:2,
                            fontSize:7, fontWeight:800, color:CLR_RED,
                            lineHeight:1, letterSpacing:'-0.02em',
                          }}>F</span>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Grid body */}
                <div style={{ position:'relative', width:timelineWidth }}>
                  {isCompact && allDays.map((d, di) => (d.dow === 1 ? (
                    <div key={`snap-${di}`} aria-hidden="true" style={{ ...Z.weekSnapMarker, left: di * dayW }} />
                  ) : null))}
                  {activeCategories.map((cat, ci) => {
                    const hdrColor = CAT_HDR_ALT[ci % CAT_HDR_ALT.length];
                    return (
                      <React.Fragment key={cat}>
                        {/* Category band */}
                        <div style={{ display:'flex', height:CAT_H, width:timelineWidth, background: hdrColor, borderBottom:'1px solid rgba(0,0,0,0.15)' }}>
                          {allDays.map((_, di) => (
                            <div key={di} style={{ width:dayW, minWidth:dayW, borderRight:'1px solid rgba(255,255,255,0.08)' }} />
                          ))}
                        </div>
                        {/* Task rows */}
                        {rowsForCategory(cat).map(row => {
                          const task = row.task;
                          if (row.isSubtask) {
                            // Grayed-out alignment strip — no bar, no day
                            // cells. Matches the subtask row on the left so
                            // vertical alignment stays intact while making
                            // it visually obvious the row has no schedule.
                            return (
                              <div
                                key={task.id}
                                style={{
                                  position:'relative', height:ROW_H, width:timelineWidth, minWidth:timelineWidth,
                                  borderBottom:'1px solid #f3efe9',
                                  background:'#F9F5F5',
                                  backgroundImage: 'repeating-linear-gradient(135deg, rgba(107,28,42,0.02) 0 8px, transparent 8px 16px)',
                                }}
                              />
                            );
                          }
                          const dp = dragPreview?.taskId === task.id ? dragPreview : null;
                          const actualInicio = dp ? dp.inicio : task.inicio;
                          const actualFin = dp ? dp.fin : task.fin;
                          const bar = getBarPixels(actualInicio, actualFin);
                          const isNotStarted = !task.progreso || task.progreso === 0;
                          return (
                            <div key={task.id} style={{ position:'relative', height:ROW_H, width:timelineWidth, minWidth:timelineWidth, borderBottom:'1px solid #f3efe9', display:'block' }}>
                              {/* Day cell grid — clickable for per-day journal */}
                              <div style={{ display:'flex', position:'absolute', top:0, left:0, width:timelineWidth, height:ROW_H, zIndex:1 }}>
                                {allDays.map((d, di) => {
                                  const we = d.dow === 0 || d.dow === 6;
                                  const fer = FERIADOS_PERU.has(d.dateStr);
                                  const off = we || fer;
                                  const dayNotes = journalByDay[`${task.id}|${d.dateStr}`];
                                  const hasDayNote = !!(dayNotes && dayNotes.length > 0);
                                  return (
                                    <div key={di}
                                      data-daycell="1"
                                      style={{
                                        width:dayW, minWidth:dayW, height:ROW_H,
                                        borderRight:'1px solid #f3efe9',
                                        background: off ? '#F2EDE5' : '#fff',
                                        cursor:'pointer',
                                        position:'relative',
                                      }}
                                      onMouseEnter={isCompact ? undefined : e => {
                                        e.currentTarget.style.background = off ? '#E8DFCF' : '#F7F3EE';
                                        cancelPopoverClose();
                                        scheduleShowCellPopover(task, d.dateStr, e.currentTarget);
                                      }}
                                      onMouseLeave={isCompact ? undefined : e => {
                                        e.currentTarget.style.background = off ? '#F2EDE5' : '#fff';
                                        cancelPopoverTimer();
                                        if (cellPopover) schedulePopoverClose();
                                      }}
                                      onClick={isCompact ? tapToSheet(task, d.dateStr) : e => {
                                        e.stopPropagation();
                                        showCellPopoverNow(task, d.dateStr, e.currentTarget);
                                      }}
                                      title={`${task.nombre} — ${fmtExcel(d.dateStr)}${fer ? ' · Feriado' : ''}${hasDayNote?` · ${dayNotes.length} nota${dayNotes.length>1?'s':''}`:''}`}
                                    >
                                      {hasDayNote && (
                                        <span style={{
                                          position:'absolute', bottom:3, left:'50%', transform:'translateX(-50%)',
                                          width:6, height:6, borderRadius:'50%', background:'#C9A96E',
                                          boxShadow:'0 0 0 1.5px #fff',
                                        }} />
                                      )}
                                    </div>
                                  );
                                })}
                              </div>

                              {/* BAR — progress-fill style */}
                              {bar && bar.width > 0 && (() => {
                                const prog = Math.max(0, Math.min(100, task.progreso ?? 0));
                                const barW = Math.max(bar.width - 4, 8);
                                const showLabel = barW > 40;
                                const durationDays = daysBetween(task.inicio, task.fin, !!task.trabaja_sabados, !!task.trabaja_domingos) || 0;
                                // Base layer: not-started = pale muted pink; otherwise light wine as "remaining"
                                const baseColor = isNotStarted ? BAR_EMPTY : BAR_LIGHT;
                                return (
                                  <div
                                    data-barzone="1"
                                    onClick={isCompact ? tapToSheet(task, null) : e => {
                                      e.stopPropagation();
                                      if (barDrag.current?.hasMoved) return;
                                      const barRect = e.currentTarget.getBoundingClientRect();
                                      const dayIdx = Math.max(0, Math.floor((e.clientX - barRect.left) / dayW));
                                      const start = toLocal(task.inicio);
                                      let dateStr = task.inicio;
                                      if (start) {
                                        const d = new Date(start);
                                        d.setDate(d.getDate() + dayIdx);
                                        dateStr = fmtDate(d);
                                      }
                                      showCellPopoverNow(task, dateStr, e.currentTarget);
                                    }}
                                    title={`${task.nombre} — ${prog}%`}
                                    style={{
                                      position:'absolute', zIndex:20,
                                      top: BAR_VPAD,
                                      left: bar.left + 2,
                                      width: barW,
                                      height: ROW_H - BAR_VPAD * 2,
                                      backgroundColor: baseColor,
                                      borderRadius: 3,
                                      border: isNotStarted ? `1px solid ${BAR_LIGHT}` : 'none',
                                      boxShadow: isNotStarted ? 'none' : '0 1px 3px rgba(0,0,0,0.15)',
                                      cursor: 'pointer',
                                      overflow: 'hidden',
                                    }}
                                  >
                                    {/* Filled portion (left → right) — only if progress > 0 */}
                                    {prog > 0 && (
                                      <div style={{
                                        position:'absolute', top:0, left:0,
                                        width: `${prog}%`, height:'100%',
                                        backgroundColor: BAR_WINE,
                                        borderRadius: prog === 100 ? 3 : '3px 0 0 3px',
                                        pointerEvents: 'none',
                                      }} />
                                    )}

                                    {/* Label */}
                                    {showLabel && (
                                      <span style={{
                                        position:'absolute', top:0, left:0, right:0, bottom:0,
                                        display:'flex', alignItems:'center', justifyContent:'center',
                                        fontSize:10, fontWeight:700,
                                        color: '#fff',
                                        textShadow: '0 1px 2px rgba(0,0,0,0.45)',
                                        pointerEvents:'none', whiteSpace:'nowrap',
                                        letterSpacing:'0.02em',
                                      }}>
                                        {durationDays}d
                                      </span>
                                    )}

                                    {/* Drag handles */}
                                    {!isCompact && (<>
                                    <div data-barzone="1" onMouseDown={e => onBarMouseDown(e, task, 'left')}
                                      style={{ position:'absolute', left:0, top:0, width:DRAG_HANDLE_W, height:'100%', cursor:'col-resize', zIndex:2 }} />
                                    <div data-barzone="1" onMouseDown={e => onBarMouseDown(e, task, 'move')}
                                      style={{ position:'absolute', left:DRAG_HANDLE_W, top:0, right:DRAG_HANDLE_W, height:'100%', cursor:'grab', zIndex:2 }} />
                                    <div data-barzone="1" onMouseDown={e => onBarMouseDown(e, task, 'right')}
                                      style={{ position:'absolute', right:0, top:0, width:DRAG_HANDLE_W, height:'100%', cursor:'col-resize', zIndex:2 }} />
                                    </>)}
                                  </div>
                                );
                              })()}
                            </div>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}

                  {/* TODAY MARKER */}
                  {todayIdx >= 0 && (
                    <div style={{
                      position:'absolute', top:0, left: todayIdx * dayW + dayW / 2 - 1,
                      width:2, height: Math.max(totalH, 200),
                      background: CLR_RED, zIndex:10, pointerEvents:'none', opacity:0.85,
                    }}>
                      <div style={{ position:'absolute', top:-5, left:-5, width:12, height:12, background: CLR_RED, borderRadius:2, transform:'rotate(45deg)' }} />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      )}
      </div>

      {/* DRAG TOOLTIP */}
      {dragPreview && (
        <div style={{ position:'fixed', left:dragPreview.x+14, top:dragPreview.y-34, background:'#2D2D2D', color:'#fff', padding:'5px 12px', borderRadius:6, fontSize:11, fontWeight:600, fontFamily:"'DM Sans',sans-serif", pointerEvents:'none', zIndex:9999, whiteSpace:'nowrap' }}>
          {fmtExcel(dragPreview.inicio)} → {fmtExcel(dragPreview.fin)}
        </div>
      )}

      {/* CELL PREVIEW POPOVER */}
      {cellPopover && (
        <div
          ref={cellPopoverRef}
          role="menu"
          onMouseEnter={cancelPopoverClose}
          onMouseLeave={() => setCellPopover(null)}
          style={{
            position:'fixed', left:cellPopover.x, top:cellPopover.y,
            width:230, zIndex:900,
            background:'#fff', borderRadius:10,
            border:'1.5px solid #6B1C2A',
            boxShadow:'0 10px 28px rgba(107,28,42,0.18), 0 0 0 1px rgba(0,0,0,0.04)',
            padding:6,
            fontFamily:"'DM Sans',sans-serif",
          }}
        >
          {/* Arrow pointing to cell */}
          <div style={{
            position:'absolute',
            ...(cellPopover.above ? { bottom:-7 } : { top:-7 }),
            left:'50%', transform:'translateX(-50%) rotate(45deg)',
            width:11, height:11, background:'#fff',
            borderRight: cellPopover.above ? '1.5px solid #6B1C2A' : 'none',
            borderBottom: cellPopover.above ? '1.5px solid #6B1C2A' : 'none',
            borderLeft: cellPopover.above ? 'none' : '1.5px solid #6B1C2A',
            borderTop: cellPopover.above ? 'none' : '1.5px solid #6B1C2A',
          }} />

          {/* Header: task + date */}
          <div style={{ padding:'6px 10px 8px', borderBottom:'1px solid #f3efe9', marginBottom:4 }}>
            <div style={{ fontSize:11, fontWeight:700, color:'var(--wine)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
              {cellPopover.task.nombre}
            </div>
            <div style={{ fontSize:10, color:'var(--text-light)', marginTop:1 }}>
              {cellPopover.dateStr && toLocal(cellPopover.dateStr).toLocaleDateString('es-MX', { weekday:'short', day:'numeric', month:'short' })}
            </div>
          </div>

          {/* Option 1: Add day note */}
          <button
            onClick={() => { const { task, dateStr } = cellPopover; setCellPopover(null); openJournalModal(task, dateStr); }}
            style={popBtnStyle}
            onMouseEnter={e => { e.currentTarget.style.background = '#FDFBF8'; e.currentTarget.style.borderLeftColor = '#6B1C2A'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderLeftColor = 'transparent'; }}
          >
            <span style={popIconStyle}>
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="#6B1C2A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 10l-4 4H3a1 1 0 01-1-1V3a1 1 0 011-1h10a1 1 0 011 1v7z"/>
                <path d="M11 2l3 3"/><line x1="5" y1="6" x2="11" y2="6"/><line x1="5" y1="9" x2="9" y2="9"/>
              </svg>
            </span>
            <span style={popLabelStyle}>Añadir nota al journal</span>
          </button>

          {/* Option 2: Edit task */}
          <button
            onClick={() => { const { task } = cellPopover; setCellPopover(null); openEditModal(task); }}
            style={popBtnStyle}
            onMouseEnter={e => { e.currentTarget.style.background = '#FDFBF8'; e.currentTarget.style.borderLeftColor = '#6B1C2A'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderLeftColor = 'transparent'; }}
          >
            <span style={popIconStyle}>
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="#6B1C2A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.5 2.5l3 3L6 13H3v-3z"/>
                <path d="M9.5 3.5l3 3"/>
              </svg>
            </span>
            <span style={popLabelStyle}>Editar tarea</span>
          </button>
        </div>
      )}

      {/* MINI PROGRESS MODAL */}
      {progressModal && (
        <div style={isMobile ? { ...Z.overlay, ...dialog.overlay } : Z.overlay} onClick={() => setProgressModal(null)}>
          <div style={isMobile ? { ...Z.modal, maxWidth:420, ...dialog.panel } : { ...Z.modal, maxWidth:420 }} onClick={e => e.stopPropagation()}>
            <div style={isMobile ? { ...Z.modalHeader, alignItems:'center', paddingLeft:16, paddingRight:8 } : Z.modalHeader}>
              <h3 style={Z.modalTitle}>Editar progreso</h3>
              <button style={isMobile ? { ...Z.closeBtn, ...sheet.closeBtn } : Z.closeBtn} onClick={() => setProgressModal(null)}>✕</button>
            </div>
            <div style={isMobile ? { padding:'12px 16px 16px' } : { padding:'16px 26px 24px' }}>
              <div style={{ fontSize:13, fontWeight:600, color:'var(--text-primary)', marginBottom:4 }}>
                {progressModal.task.nombre}
              </div>
              <div style={{ fontSize:11, color:'var(--text-light)', marginBottom:18 }}>
                {progressModal.task.asignado || '—'} · {progressModal.task.categoria}
              </div>

              <div style={{ display:'flex', alignItems:'baseline', gap:8, marginBottom:8 }}>
                <span style={{ fontSize:36, fontWeight:700, color:'var(--wine)', fontFamily:"'Cormorant Garamond',serif", lineHeight:1 }}>
                  {progressModal.value}
                </span>
                <span style={{ fontSize:14, fontWeight:600, color:'var(--wine)' }}>%</span>
              </div>
              <input type="range" min="0" max="100" value={progressModal.value}
                onChange={e => setProgressModal(prev => ({ ...prev, value: Number(e.target.value) }))}
                style={{ width:'100%', accentColor:'#6B1C2A', marginTop:4 }}
              />
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:10, color:'var(--text-light)', marginTop:4 }}>
                <span>0%</span><span>50%</span><span>100%</span>
              </div>

              {/* Quick buttons */}
              <div style={{ display:'flex', gap:6, marginTop:14 }}>
                {[0, 25, 50, 75, 100].map(v => (
                  <button key={v} type="button"
                    onClick={() => setProgressModal(prev => ({ ...prev, value: v }))}
                    style={isCompact ? {
                      flex:1, padding:'6px 0', fontSize:13, fontWeight:700, minHeight:TAP_TARGET,
                      borderRadius:6, cursor:'pointer', fontFamily:"'DM Sans',sans-serif",
                      border: progressModal.value === v ? '1.5px solid #6B1C2A' : '1px solid var(--border)',
                      background: progressModal.value === v ? 'rgba(107,28,42,0.08)' : '#fff',
                      color: progressModal.value === v ? '#6B1C2A' : 'var(--text-secondary)',
                    } : {
                      flex:1, padding:'6px 0', fontSize:11, fontWeight:700,
                      borderRadius:6, cursor:'pointer', fontFamily:"'DM Sans',sans-serif",
                      border: progressModal.value === v ? '1.5px solid #6B1C2A' : '1px solid var(--border)',
                      background: progressModal.value === v ? 'rgba(107,28,42,0.08)' : '#fff',
                      color: progressModal.value === v ? '#6B1C2A' : 'var(--text-secondary)',
                    }}>
                    {v}%
                  </button>
                ))}
              </div>

              <div style={isMobile ? { ...Z.modalActions, marginTop:18, flexWrap:'wrap' } : { ...Z.modalActions, marginTop:18 }}>
                <button type="button" style={isMobile ? { ...Z.cancelBtn, ...Z.btnMobile } : Z.cancelBtn} onClick={() => setProgressModal(null)}>Cancelar</button>
                <button type="button" style={isMobile ? { ...Z.submitBtn, ...Z.btnMobile } : Z.submitBtn} onClick={saveProgress} disabled={progressSaving}>
                  {progressSaving ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* DELETE-ALL CONFIRMATION MODAL */}
      {showDeleteAllModal && (
        <div style={isMobile ? { ...Z.confirmOverlay, ...dialog.overlay } : Z.confirmOverlay} onClick={() => { if (!templateDeleting) setShowDeleteAllModal(false); }}>
          <div style={isMobile ? { ...Z.confirmModal, ...dialog.panel } : Z.confirmModal} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
            <h3 style={Z.confirmTitle}>¿Eliminar todas las tareas?</h3>
            <p style={isMobile ? { ...Z.confirmMessage, fontSize:15 } : Z.confirmMessage}>
              Se eliminarán todas las tareas de este cronograma. Esta acción no se puede deshacer.
            </p>
            <div style={isMobile ? { ...Z.confirmActions, flexWrap:'wrap' } : Z.confirmActions}>
              <button
                type="button"
                style={isMobile ? { ...Z.confirmCancelBtn, ...Z.btnMobile } : Z.confirmCancelBtn}
                onClick={() => setShowDeleteAllModal(false)}
                disabled={templateDeleting}
              >
                Cancelar
              </button>
              <button
                type="button"
                style={isMobile ? { ...Z.confirmDangerBtn, ...Z.btnMobile } : Z.confirmDangerBtn}
                onClick={confirmDeleteAll}
                disabled={templateDeleting}
                autoFocus
              >
                {templateDeleting ? 'Eliminando…' : 'Eliminar todo'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TOAST */}
      {toast && (
        <div style={isMobile ? { position:'fixed', bottom:28, left:'50%', transform:'translateX(-50%)',
          background: toast.type === 'error' ? '#B83B3B' : '#4A7C59',
          color:'#fff', padding:'10px 24px', borderRadius:10, fontSize:13, fontWeight:600,
          fontFamily:"'DM Sans',sans-serif", zIndex:9999, boxShadow:'0 4px 20px rgba(0,0,0,0.2)', ...toastMobile } : { position:'fixed', bottom:28, left:'50%', transform:'translateX(-50%)',
          background: toast.type === 'error' ? '#B83B3B' : '#4A7C59',
          color:'#fff', padding:'10px 24px', borderRadius:10, fontSize:13, fontWeight:600,
          fontFamily:"'DM Sans',sans-serif", zIndex:9999, boxShadow:'0 4px 20px rgba(0,0,0,0.2)' }}>
          {toast.message}
        </div>
      )}

      {/* NOTES + AI SUMMARY (two-column on wide screens) */}
      {!loading && tasks.length > 0 && (
        <div style={isCompact ? { ...Z.bottomPanels, ...Z.bottomPanelsCompact } : Z.bottomPanels}>
          <div style={{ background:'#fff', borderRadius:10, border:'1px solid var(--border)', overflow:'hidden' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'16px 22px 12px' }}>
              <h3 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:20, fontWeight:600, color:'var(--wine)', margin:0 }}>Notas Recientes</h3>
              {!journalReady && (
                <span style={{ fontSize:11, color:CLR_ORANGE, fontWeight:600, background:'rgba(196,146,42,0.1)', padding:'3px 10px', borderRadius:6 }}>
                  Ejecuta supabase-journal-migration.sql
                </span>
              )}
              {journalReady && <span style={{ fontSize:11, color:'var(--text-light)' }}>{allJournals.length} nota{allJournals.length!==1?'s':''}</span>}
            </div>
            {journalReady && allJournals.length > 0 ? (
              <div style={{ padding:'0 22px 18px' }}>
                {allJournals.slice(0, 5).map((j, i) => {
                  const taskName = tasks.find(t => t.id === j.tarea_id)?.nombre || 'Tarea';
                  return (
                    <div key={j.id || i} style={{ display:'flex', gap:12, padding:'10px 0', borderTop: i > 0 ? '1px solid #f3efe9' : 'none' }}>
                      <div style={{ width:5, flexShrink:0, borderRadius:2, background:'#C9A96E', marginTop:3 }} />
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={isMobile ? { display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:3, flexWrap:'wrap', gap:2 } : { display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:3 }}>
                          <span style={isMobile ? { fontSize:15, fontWeight:600, color:'var(--text-primary)' } : { fontSize:12, fontWeight:600, color:'var(--text-primary)' }}>{taskName}</span>
                          <span style={isMobile ? { fontSize:13, color:'var(--text-light)' } : { fontSize:10, color:'var(--text-light)', marginLeft:8 }}>{j.fecha && fmtExcel(j.fecha)} · {j.autor || 'Sin autor'}</span>
                        </div>
                        <p style={isMobile ? { fontSize:15, color:'var(--text-secondary)', lineHeight:1.45, margin:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' } : { fontSize:12, color:'var(--text-secondary)', lineHeight:1.45, margin:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{j.nota}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : journalReady ? (
              <p style={Z.emptyMsg}>No hay notas. Haz clic en el icono de nota junto a cualquier tarea.</p>
            ) : (
              <p style={Z.emptyMsg}>Ejecuta la migración SQL para habilitar las notas.</p>
            )}
          </div>

          {/* AI SUMMARY INLINE PANEL */}
          <div style={Z.aiInlinePanel}>
            <div style={Z.aiInlineHeader}>
              <div>
                <h3 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:20, fontWeight:600, color:'var(--wine)', margin:0 }}>
                  ✨ Resumen IA
                </h3>
                <div style={Z.aiInlineDate}>
                  {panelSummary?.fecha
                    ? formatAiPanelDate(panelSummary.fecha)
                    : (selectedProject === 'all' ? 'Selecciona un proyecto' : 'Hoy')}
                </div>
              </div>
              <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                {selectedProject !== 'all' && (
                  <button
                    type="button"
                    style={isMobile ? { ...Z.aiInlineRegen, ...Z.tapCompact } : Z.aiInlineRegen}
                    onClick={() => generateAiSummary(selectedProject, true)}
                    disabled={aiLoading || panelLoading}
                    title="Regenerar"
                  >
                    {aiLoading ? '…' : '↻ Regenerar'}
                  </button>
                )}
                <button
                  type="button"
                  style={isMobile ? { ...Z.aiInlineCollapse, ...Z.tapCompact } : Z.aiInlineCollapse}
                  onClick={() => setPanelCollapsed(c => !c)}
                  aria-label={panelCollapsed ? 'Mostrar resumen' : 'Ocultar resumen'}
                  title={panelCollapsed ? 'Mostrar' : 'Ocultar'}
                >
                  {panelCollapsed ? '▾' : '▴'}
                </button>
              </div>
            </div>

            {!panelCollapsed && (
              <div style={Z.aiInlineBody}>
                {selectedProject === 'all' ? (
                  <p style={isMobile ? { ...Z.aiInlineEmpty, ...Z.textMobile } : Z.aiInlineEmpty}>Selecciona un proyecto para ver su resumen.</p>
                ) : panelLoading ? (
                  <p style={isMobile ? { ...Z.aiInlineEmpty, ...Z.textMobile } : Z.aiInlineEmpty}>Cargando resumen…</p>
                ) : aiLoading && !panelSummary ? (
                  <p style={isMobile ? { ...Z.aiInlineEmpty, ...Z.textMobile } : Z.aiInlineEmpty}>Analizando proyecto con IA…</p>
                ) : !panelSummary ? (
                  <p style={isMobile ? { ...Z.aiInlineEmpty, ...Z.textMobile } : Z.aiInlineEmpty}>Sin resumen aún — usa el botón ↻ Regenerar para crear uno.</p>
                ) : (
                  <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
                    <section>
                      <h4 style={Z.aiInlineSectionTitle}>Estado general</h4>
                      <p style={isMobile ? { ...Z.aiInlineParagraph, ...Z.textMobile } : Z.aiInlineParagraph}>{panelSummary.resumen || '—'}</p>
                    </section>
                    <AiInlineList title="Logros" items={panelSummary.logros} color="#4A7C59" bullet="•" />
                    <AiInlineList title="Problemas" items={panelSummary.problemas} color="#B83B3B" bullet="•" />
                    <AiInlineList title="Recomendaciones" items={panelSummary.recomendaciones} color="#6B1C2A" bullet="•" />
                    <AiInlineList title="Próximos pasos" items={panelSummary.proximos_pasos} color="#C97B32" numbered />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* JOURNAL MODAL */}
      {journalModal && (
        <div style={isMobile ? { ...Z.overlay, ...sheet.overlay } : Z.overlay} onClick={() => setJournalModal(null)}>
          <div style={isMobile ? { ...Z.modal, ...Z.modalMobile } : { ...Z.modal, maxWidth:620 }} onClick={e => e.stopPropagation()}>
            <div style={isMobile ? { ...Z.modalHeader, ...Z.modalHeaderMobile } : Z.modalHeader}>
              <div>
                <h3 style={Z.modalTitle}>
                  {journalModal.dateStr ? 'Nota del día' : 'Notas de tarea'}
                </h3>
                <p style={Z.modalSubtitle}>
                  {journalModal.task.nombre}
                  {journalModal.dateStr && (
                    <> — <strong style={{ color:'var(--wine)' }}>{toLocal(journalModal.dateStr).toLocaleDateString('es-MX', { weekday:'long', day:'numeric', month:'long', year:'numeric' })}</strong></>
                  )}
                </p>
              </div>
              <button style={isMobile ? { ...Z.closeBtn, ...sheet.closeBtn } : Z.closeBtn} onClick={() => setJournalModal(null)}>✕</button>
            </div>
            <div style={isMobile ? { ...Z.journalBody, ...Z.journalBodyMobile } : Z.journalBody}>
              <div style={{ display:'flex', flexDirection:'column', gap:10, marginBottom:20, padding:16, background:'#FDFBF8', borderRadius:10, border:'1px solid var(--border)' }}>
                <span style={{ fontSize:12, fontWeight:600, color:'var(--text-secondary)' }}>
                  Nueva nota — {journalModal.dateStr
                    ? toLocal(journalModal.dateStr).toLocaleDateString('es-MX', { day:'numeric', month:'long', year:'numeric' })
                    : new Date().toLocaleDateString('es-MX', { day:'numeric', month:'long', year:'numeric' })}
                </span>
                <textarea style={Z.textarea} placeholder="Escribe lo que pasó hoy con esta tarea..." value={journalText} onChange={e => setJournalText(e.target.value)} rows={3} />
                <div style={isMobile ? { display:'flex', justifyContent:'space-between', alignItems:'center', ...Z.journalNewRowMobile } : { display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <span style={{ fontSize:11, color:'var(--text-light)' }}>Autor:</span>
                    <input style={{ ...Z.input, width:160, padding:'6px 10px' }} value={journalAutor} onChange={e => setJournalAutor(e.target.value)} />
                  </div>
                  <button style={isMobile ? {...Z.submitBtn, opacity:journalText.trim()?1:0.4, padding:'7px 20px', ...Z.btnMobile} : {...Z.submitBtn, opacity:journalText.trim()?1:0.4, padding:'7px 20px'}} onClick={saveJournal} disabled={journalSaving||!journalText.trim()}>
                    {journalSaving ? 'Guardando...' : 'Guardar nota'}
                  </button>
                </div>
              </div>
              {journalEntries.length > 0 ? (
                <div>
                  <span style={{ fontSize:12, fontWeight:600, color:'var(--text-secondary)', marginBottom:10, display:'block' }}>
                    {journalModal.dateStr ? `Notas de este día (${journalEntries.length})` : `Todas las notas (${journalEntries.length})`}
                  </span>
                  <div style={isMobile ? { ...Z.journalEntries, ...Z.journalEntriesMobile } : Z.journalEntries}>
                    {journalEntries.map((en, i) => (
                      <div key={en.id||i} style={Z.journalEntry}>
                        <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6, alignItems:'center' }}>
                          <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                            <span style={{ width:6, height:6, borderRadius:'50%', background:'#C9A96E' }} />
                            <span style={{ fontSize:12, fontWeight:600, color:'var(--wine)' }}>{en.autor||'Sin autor'}</span>
                          </div>
                          <span style={{ fontSize:10, color:'var(--text-light)' }}>
                            {en.fecha && fmtExcel(en.fecha)} · {en.created_at && new Date(en.created_at).toLocaleTimeString('es-MX',{hour:'2-digit',minute:'2-digit'})}
                          </span>
                        </div>
                        <p style={{ fontSize:13, color:'var(--text-primary)', lineHeight:1.6, margin:0, whiteSpace:'pre-wrap' }}>{en.nota}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : <p style={Z.emptyMsg}>No hay notas. Escribe la primera arriba.</p>}
            </div>
          </div>
        </div>
      )}

      {/* EDIT TASK MODAL */}
      {editTask && (
        <div style={isMobile ? { ...Z.overlay, ...sheet.overlay } : Z.overlay} onClick={() => setEditTask(null)}>
          <div style={isMobile ? { ...Z.modal, ...Z.modalMobile } : { ...Z.modal, maxWidth:620 }} onClick={e => e.stopPropagation()}>
            <div style={isMobile ? { ...Z.modalHeader, ...Z.modalHeaderMobile } : Z.modalHeader}>
              <h3 style={Z.modalTitle}>Editar Tarea</h3>
              <button style={isMobile ? { ...Z.closeBtn, ...sheet.closeBtn } : Z.closeBtn} onClick={() => setEditTask(null)}>✕</button>
            </div>
            <form onSubmit={handleEditSubmit} style={isMobile ? { ...Z.form, ...Z.formMobile } : Z.form}>
              <div style={isMobile ? { ...Z.fieldRow, ...Z.fieldRowMobile } : Z.fieldRow}>
                <label style={Z.fieldGroup}><span style={Z.fieldLabel}>Nombre de la tarea</span>
                  <input style={Z.input} name="nombre" value={editForm.nombre} onChange={handleEditChange} required /></label>
                <label style={Z.fieldGroup}><span style={Z.fieldLabel}>Asignado a</span>
                  <select style={Z.input} name="asignado" value={editForm.asignado} onChange={handleEditChange} required>
                    <option value="">— Sin asignar —</option>
                    <AsignadoOptions options={asignadoOptions} />
                    {editForm.asignado && !asignadoOptions.find(o => o.value === editForm.asignado) && (
                      <option value={editForm.asignado}>{editForm.asignado} (externo)</option>
                    )}
                  </select>
                </label>
              </div>

              <div style={isMobile ? { ...Z.fieldRow, ...Z.fieldRowMobile } : Z.fieldRow}>
                <label style={Z.fieldGroup}><span style={Z.fieldLabel}>Categoría</span>
                  <select style={Z.input} name="categoria" value={editForm.categoria} onChange={handleEditChange} required>
                    <option value="">— Selecciona categoría —</option>
                    {ORDERED_KEYS.map(k => <option key={k} value={k}>{k}</option>)}
                    {editForm.categoria && !ORDERED_KEYS.includes(editForm.categoria) && (
                      <option value={editForm.categoria}>{editForm.categoria}</option>
                    )}
                  </select></label>
                <label style={Z.fieldGroup}><span style={Z.fieldLabel}>Estado</span>
                  <select style={Z.input} name="estado" value={editForm.estado} onChange={handleEditChange}>
                    <option>No iniciado</option>
                    <option>En progreso</option>
                    <option>Completado</option>
                    <option>Retrasado</option>
                  </select></label>
              </div>

              {/* Planned dates — read only */}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, padding:'10px 12px', background:'#FDFBF8', borderRadius:8, border:'1px dashed var(--border)' }}>
                <div>
                  <span style={{ ...Z.fieldLabel, fontSize:10 }}>INICIO PLANIFICADO</span>
                  <div style={{ fontSize:13, fontWeight:600, color:'var(--text-secondary)', marginTop:3 }}>
                    {fmtExcel(editTask.fecha_inicio_plan) || fmtExcel(editTask.inicio) || '—'}
                  </div>
                </div>
                <div>
                  <span style={{ ...Z.fieldLabel, fontSize:10 }}>FIN PLANIFICADO</span>
                  <div style={{ fontSize:13, fontWeight:600, color:'var(--text-secondary)', marginTop:3 }}>
                    {fmtExcel(editTask.fecha_fin_plan) || fmtExcel(editTask.fin) || '—'}
                  </div>
                </div>
              </div>

              {/* Actual dates — editable */}
              <div style={isMobile ? { ...Z.fieldRow, ...Z.fieldRowMobile } : Z.fieldRow}>
                <label style={Z.fieldGroup}><span style={Z.fieldLabel}>Inicio real</span>
                  <input style={Z.input} type="date" name="inicio" value={editForm.inicio} onChange={handleEditChange} /></label>
                <label style={Z.fieldGroup}><span style={Z.fieldLabel}>Fin real</span>
                  <input style={Z.input} type="date" name="fin" value={editForm.fin} onChange={handleEditChange} /></label>
              </div>

              {/* Days counter — auto, respects the task's weekend toggles */}
              <div style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 14px', background:'#F7F3EE', borderRadius:8 }}>
                <span style={{ ...Z.fieldLabel, fontSize:11 }}>Duración:</span>
                <span style={{ fontSize:16, fontWeight:700, color:'var(--wine)' }}>
                  {(() => {
                    const d = daysBetween(editForm.inicio, editForm.fin, !!editForm.trabaja_sabados, !!editForm.trabaja_domingos) || 0;
                    return `${d} día${d === 1 ? '' : 's'}`;
                  })()}
                </span>
              </div>

              <div style={Z.workWeekendRow}>
                <label style={Z.workWeekendLabel}>
                  <input type="checkbox" name="trabaja_sabados" checked={!!editForm.trabaja_sabados} onChange={handleEditChange} style={Z.workWeekendCheck} />
                  Trabaja sábados
                </label>
                <label style={Z.workWeekendLabel}>
                  <input type="checkbox" name="trabaja_domingos" checked={!!editForm.trabaja_domingos} onChange={handleEditChange} style={Z.workWeekendCheck} />
                  Trabaja domingos
                </label>
              </div>

              {(() => {
                const subStats = editTask ? subtaskStats(editTask.id) : { total: 0, done: 0 };
                const hasSubs = subStats.total > 0;
                return (
                  <label style={Z.fieldGroup}>
                    <span style={Z.fieldLabel}>
                      Progreso: {hasSubs ? Math.round((subStats.done / subStats.total) * 100) : editForm.progreso}%
                      {hasSubs && (
                        <span style={{ marginLeft:8, fontSize:10, fontWeight:600, color:'var(--text-light)' }}>
                          · {subStats.total} subtarea{subStats.total === 1 ? '' : 's'} · {subStats.done} completada{subStats.done === 1 ? '' : 's'} (auto)
                        </span>
                      )}
                    </span>
                    <input
                      type="range"
                      name="progreso"
                      min="0" max="100"
                      value={hasSubs ? Math.round((subStats.done / subStats.total) * 100) : editForm.progreso}
                      onChange={handleEditChange}
                      disabled={hasSubs}
                      style={{ width:'100%', accentColor:'#6B1C2A', opacity: hasSubs ? 0.5 : 1 }}
                    />
                  </label>
                );
              })()}

              <label style={Z.fieldGroup}>
                <span style={Z.fieldLabel}>Notas de la tarea</span>
                <textarea style={Z.textarea} name="notas" value={editForm.notas} onChange={handleEditChange} rows={3} placeholder="Observaciones generales de la tarea (no diarias)..." />
              </label>

              <div style={isMobile ? { ...Z.modalActions, ...Z.modalActionsMobile } : Z.modalActions}>
                <button
                  type="button"
                  style={isMobile ? { ...Z.deleteBtn, ...Z.btnMobile } : Z.deleteBtn}
                  onClick={handleDeleteTask}
                  disabled={editSaving}
                >
                  Eliminar tarea
                </button>
                <span style={{ flex: 1 }} />
                <button type="button" style={isMobile ? { ...Z.cancelBtn, ...Z.btnMobile } : Z.cancelBtn} onClick={() => setEditTask(null)} disabled={editSaving}>Cancelar</button>
                <button type="submit" style={isMobile ? { ...Z.submitBtn, ...Z.btnMobile } : Z.submitBtn} disabled={editSaving}>{editSaving?'Guardando...':'Guardar Cambios'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* NEW TASK MODAL */}
      {showTaskModal && (
        <div style={isMobile ? { ...Z.overlay, ...sheet.overlay } : Z.overlay} onClick={() => setShowTaskModal(false)}>
          <div style={isMobile ? { ...Z.modal, ...Z.modalMobile } : Z.modal} onClick={e => e.stopPropagation()}>
            <div style={isMobile ? { ...Z.modalHeader, ...Z.modalHeaderMobile } : Z.modalHeader}>
              <h3 style={Z.modalTitle}>Nueva Tarea</h3>
              <button style={isMobile ? { ...Z.closeBtn, ...sheet.closeBtn } : Z.closeBtn} onClick={() => setShowTaskModal(false)}>✕</button>
            </div>
            <form onSubmit={handleTaskSubmit} style={isMobile ? { ...Z.form, ...Z.formMobile } : Z.form}>
              <label style={Z.fieldGroup}><span style={Z.fieldLabel}>Proyecto</span>
                <select style={Z.input} name="proyecto_id" value={taskForm.proyecto_id} onChange={handleTaskChange} required>
                  <option value="">Seleccionar...</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                </select></label>
              <div style={isMobile ? { ...Z.fieldRow, ...Z.fieldRowMobile } : Z.fieldRow}>
                <label style={Z.fieldGroup}><span style={Z.fieldLabel}>Nombre</span><input style={Z.input} name="nombre" value={taskForm.nombre} onChange={handleTaskChange} required /></label>
                <label style={Z.fieldGroup}><span style={Z.fieldLabel}>Asignado a</span>
                  <select style={Z.input} name="asignado" value={taskForm.asignado} onChange={handleTaskChange} required>
                    <option value="">— Sin asignar —</option>
                    <AsignadoOptions options={asignadoOptions} />
                  </select>
                </label>
              </div>
              <div style={isMobile ? { ...Z.fieldRow, ...Z.fieldRowMobile } : Z.fieldRow}>
                <label style={Z.fieldGroup}><span style={Z.fieldLabel}>Categoría</span>
                  <select style={Z.input} name="categoria" value={taskForm.categoria} onChange={handleTaskChange} required>
                    <option value="">— Selecciona categoría —</option>
                    {ORDERED_KEYS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select></label>
                <label style={Z.fieldGroup}><span style={Z.fieldLabel}>Progreso: {taskForm.progreso}%</span>
                  <input type="range" name="progreso" min="0" max="100" value={taskForm.progreso} onChange={handleTaskChange} style={{ width:'100%', accentColor:'#6B1C2A' }} /></label>
              </div>
              {taskForm.categoria && manualRecsForTaskForm.length > 0 && (
                <div style={Z.manualRecsPanel}>
                  <div style={Z.manualRecsTitle}>
                    💡 Recomendaciones del manual para <strong>{taskForm.categoria}</strong>
                  </div>
                  <ul style={Z.manualRecsList}>
                    {manualRecsForTaskForm.map(m => (
                      <li key={m.id} style={Z.manualRecsItem}>
                        <span style={Z.manualRecsBullet}>✨</span>
                        <span style={{ flex:1 }}>{m.recomendacion_ia}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div style={isMobile ? { ...Z.fieldRow, ...Z.fieldRowMobile } : Z.fieldRow}>
                <label style={Z.fieldGroup}><span style={Z.fieldLabel}>Inicio</span><input style={Z.input} type="date" name="inicio" value={taskForm.inicio} onChange={handleTaskChange} /></label>
                <label style={Z.fieldGroup}><span style={Z.fieldLabel}>Fin</span><input style={Z.input} type="date" name="fin" value={taskForm.fin} onChange={handleTaskChange} /></label>
              </div>
              <div style={Z.workWeekendRow}>
                <label style={Z.workWeekendLabel}>
                  <input type="checkbox" name="trabaja_sabados" checked={!!taskForm.trabaja_sabados} onChange={handleTaskChange} style={Z.workWeekendCheck} />
                  Trabaja sábados
                </label>
                <label style={Z.workWeekendLabel}>
                  <input type="checkbox" name="trabaja_domingos" checked={!!taskForm.trabaja_domingos} onChange={handleTaskChange} style={Z.workWeekendCheck} />
                  Trabaja domingos
                </label>
              </div>
              <div style={isMobile ? { ...Z.modalActions, ...Z.modalActionsMobile } : Z.modalActions}>
                <button type="button" style={isMobile ? { ...Z.cancelBtn, ...Z.btnMobile } : Z.cancelBtn} onClick={() => setShowTaskModal(false)}>Cancelar</button>
                <button type="submit" style={isMobile ? { ...Z.submitBtn, ...Z.btnMobile } : Z.submitBtn} disabled={taskSaving}>{taskSaving?'Guardando...':'Crear'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* TASK ACTION SHEET (compact viewports only) */}
      {isCompact && renderTaskSheet()}

      {/* SUBTASK MINI MODAL */}
      {subtaskParent && (
        <div style={isMobile ? { ...Z.subtaskOverlay, ...sheet.overlay } : Z.subtaskOverlay} onClick={closeSubtaskModal}>
          <div style={isMobile ? { ...Z.subtaskModal, ...Z.modalMobile } : Z.subtaskModal} onClick={e => e.stopPropagation()}>
            <div style={isMobile ? { ...Z.modalHeader, ...Z.modalHeaderMobile } : { ...Z.modalHeader, padding: '20px 24px 6px' }}>
              <div>
                <h3 style={Z.modalTitle}>Nueva subtarea</h3>
                <p style={Z.modalSubtitle}>Para: <strong style={{ color:'var(--wine)' }}>{subtaskParent.nombre}</strong></p>
              </div>
              <button type="button" style={isMobile ? { ...Z.closeBtn, ...sheet.closeBtn } : Z.closeBtn} onClick={closeSubtaskModal}>✕</button>
            </div>
            <form onSubmit={saveSubtask} style={isMobile ? { ...Z.form, ...Z.formMobile } : Z.form}>
              <label style={Z.fieldGroup}>
                <span style={Z.fieldLabel}>Nombre</span>
                <input
                  style={Z.input}
                  value={subtaskForm.nombre}
                  onChange={(e) => setSubtaskForm(p => ({ ...p, nombre: e.target.value }))}
                  required
                  autoFocus
                />
              </label>
              <label style={Z.fieldGroup}>
                <span style={Z.fieldLabel}>Asignado</span>
                <select
                  style={Z.input}
                  value={subtaskForm.asignado}
                  onChange={(e) => setSubtaskForm(p => ({ ...p, asignado: e.target.value }))}
                >
                  <option value="">— Selecciona —</option>
                  <AsignadoOptions options={asignadoOptions} />
                </select>
              </label>
              <div style={isMobile ? { ...Z.fieldRow, ...Z.fieldRowMobile } : Z.fieldRow}>
                <label style={Z.fieldGroup}>
                  <span style={Z.fieldLabel}>Inicio</span>
                  <input style={Z.input} type="date" value={subtaskForm.inicio} onChange={(e) => setSubtaskForm(p => ({ ...p, inicio: e.target.value }))} />
                </label>
                <label style={Z.fieldGroup}>
                  <span style={Z.fieldLabel}>Fin</span>
                  <input style={Z.input} type="date" value={subtaskForm.fin} onChange={(e) => setSubtaskForm(p => ({ ...p, fin: e.target.value }))} />
                </label>
              </div>
              <div style={isMobile ? { ...Z.modalActions, ...Z.modalActionsMobile } : Z.modalActions}>
                <button type="button" style={isMobile ? { ...Z.cancelBtn, ...Z.btnMobile } : Z.cancelBtn} onClick={closeSubtaskModal} disabled={subtaskSaving}>Cancelar</button>
                <button type="submit" style={isMobile ? { ...Z.submitBtn, ...Z.btnMobile } : Z.submitBtn} disabled={subtaskSaving}>
                  {subtaskSaving ? 'Guardando…' : 'Crear subtarea'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Styles ── */
const popBtnStyle = {
  display:'flex', alignItems:'center', gap:10,
  width:'100%', padding:'8px 10px', borderRadius:7,
  border:'none', borderLeft:'3px solid transparent',
  background:'transparent', cursor:'pointer',
  textAlign:'left', fontFamily:"'DM Sans',sans-serif",
  transition:'background 0.15s, border-color 0.15s',
};
const popIconStyle = {
  width:26, height:26, borderRadius:6,
  background:'rgba(107,28,42,0.08)',
  display:'flex', alignItems:'center', justifyContent:'center',
  flexShrink:0,
};
const popLabelStyle = {
  fontSize:12, fontWeight:600, color:'var(--text-primary)',
};

const Z = {
  container: { padding:'28px 32px', maxWidth:1600 },

  backBar: { display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 },
  backBtn: { display:'inline-flex', alignItems:'center', gap:8, padding:'7px 14px', borderRadius:8, border:'1px solid var(--border)', background:'#fff', color:'var(--wine)', fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:"'DM Sans',sans-serif" },
  typePill: { fontSize:12, fontWeight:700, padding:'6px 14px', borderRadius:20, letterSpacing:'0.02em' },
  typePillDiseno: { background:'rgba(107,28,42,0.1)', color:'#6B1C2A', border:'1px solid rgba(107,28,42,0.25)' },
  typePillObra:   { background:'rgba(62,42,26,0.1)',  color:'#3E2A1A', border:'1px solid rgba(62,42,26,0.25)' },


  /* Excel header */
  excelHeader: { display:'flex', flexDirection:'column', background:'#fff', border:'1px solid var(--border)', borderRadius:10, padding:'18px 22px', marginBottom:14, gap:14 },
  excelHeaderTop: { display:'flex', justifyContent:'space-between', alignItems:'stretch', gap:20 },
  hdrObsWrap: { display:'flex', flexDirection:'column', gap:6, paddingTop:12, borderTop:'1px solid #f3efe9' },
  hdrObsHeader: { display:'flex', justifyContent:'space-between', alignItems:'center', gap:12 },
  hdrObsTextarea: {
    width:'100%', boxSizing:'border-box',
    padding:'9px 11px', borderRadius:7,
    border:'1px solid var(--border)', background:'#FDFBF8',
    fontSize:13, fontFamily:"'DM Sans',sans-serif",
    color:'var(--text-primary)', outline:'none',
    resize:'vertical', minHeight:54,
    lineHeight:1.5,
  },
  hdrLeft: { flex:1, display:'flex', flexDirection:'column', gap:6, minWidth:0 },
  hdrTitle: { fontFamily:"'DM Sans',sans-serif", fontSize:22, fontWeight:800, color:'#2D2D2D', margin:0, letterSpacing:'-0.01em', marginBottom:6 },
  hdrRow: { display:'flex', alignItems:'center', gap:14 },
  hdrDurationRow: {
    display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
    marginTop: 8,
  },
  hdrDurationBadge: {
    fontSize: 12.5, color: '#6B1C2A',
    background: 'rgba(107,28,42,0.08)',
    border: '1px solid rgba(107,28,42,0.20)',
    padding: '5px 12px', borderRadius: 999,
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  hdrDurationMeta: {
    fontSize: 11.5, color: 'var(--text-secondary)',
    whiteSpace: 'nowrap',
  },
  hdrRowRight: { display:'flex', alignItems:'center', gap:14, justifyContent:'flex-end' },
  hdrLabel: { fontSize:10, fontWeight:700, color:'var(--text-light)', textTransform:'uppercase', letterSpacing:'0.08em', minWidth:140 },
  hdrValue: { fontSize:13, fontWeight:600, color:'var(--text-primary)', padding:'4px 12px', background:'#F7F3EE', borderRadius:6, minWidth:120 },
  hdrRight: { display:'flex', flexDirection:'column', alignItems:'flex-end', gap:6 },
  logoBlock: { width:90, height:60, background:'#6B1C2A', borderRadius:6, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', color:'#fff', marginBottom:6 },
  logoText: { fontFamily:"'Cormorant Garamond',serif", fontSize:28, fontWeight:700, fontStyle:'italic', lineHeight:1, color:'#C9A96E' },
  logoSub: { fontSize:8, fontWeight:600, letterSpacing:'0.08em', marginTop:2, color:'#fff', opacity:0.9 },

  /* Controls */
  controls: { display:'flex', alignItems:'center', gap:10, marginBottom:12, flexWrap:'wrap' },
  projectSelect: { padding:'7px 12px', borderRadius:6, border:'1px solid var(--border)', fontSize:13, fontFamily:"'DM Sans',sans-serif", background:'#fff', cursor:'pointer', outline:'none', color:'var(--text-primary)' },
  searchWrap: { position:'relative', display:'flex', alignItems:'center', minWidth:260 },
  searchIcon: { position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:'var(--text-light)', display:'flex', alignItems:'center', pointerEvents:'none' },
  searchInput: {
    width:'100%', padding:'7px 30px 7px 30px', borderRadius:6,
    border:'1px solid var(--border)', background:'#fff',
    fontSize:13, fontFamily:"'DM Sans',sans-serif", color:'var(--text-primary)',
    outline:'none', boxSizing:'border-box',
  },
  searchClearBtn: {
    position:'absolute', right:6, top:'50%', transform:'translateY(-50%)',
    width:18, height:18, borderRadius:9, border:'none',
    background:'rgba(107,28,42,0.08)', color:'#6B1C2A',
    fontSize:9, cursor:'pointer',
    display:'flex', alignItems:'center', justifyContent:'center', padding:0,
  },
  navBtn: { width:30, height:30, borderRadius:6, border:'1px solid var(--border)', background:'#fff', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:'var(--text-secondary)' },
  zoomGroup: {
    display:'inline-flex', alignItems:'center', gap:4,
    padding:'2px 4px', borderRadius:8,
    border:'1px solid var(--border)', background:'#fff',
    marginRight:4,
  },
  zoomBtn: {
    width:24, height:24, borderRadius:5,
    border:'none', background:'transparent', cursor:'pointer',
    fontSize:16, fontWeight:700, color:'var(--text-secondary)',
    display:'flex', alignItems:'center', justifyContent:'center',
    lineHeight:1,
  },
  zoomSelect: {
    border:'none', background:'transparent', outline:'none',
    fontSize:12, fontWeight:600, color:'var(--wine)',
    fontFamily:"'DM Sans',sans-serif", cursor:'pointer',
    padding:'2px 4px', minWidth:70, textAlignLast:'center',
  },
  todayBtn: { padding:'4px 14px', borderRadius:6, border:'1.5px solid '+CLR_RED, background:'transparent', color:CLR_RED, fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:"'DM Sans',sans-serif" },
  navRange: { flex:1, fontSize:11, color:'var(--text-secondary)', fontWeight:500, textAlign:'center' },
  addBtn: { padding:'7px 18px', borderRadius:6, border:'none', background:'#6B1C2A', color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:"'DM Sans',sans-serif" },
  exportPdfBtn: {
    display:'inline-flex', alignItems:'center', gap:7,
    padding:'7px 14px', borderRadius:6,
    border:'1px solid '+CLR_RED, background:'#fff', color:CLR_RED,
    fontSize:12, fontWeight:600, cursor:'pointer',
    fontFamily:"'DM Sans',sans-serif", whiteSpace:'nowrap',
  },
  exportSpinner: {
    width:12, height:12, borderRadius:'50%',
    border:'2px solid rgba(107,28,42,0.25)',
    borderTopColor:'#6B1C2A',
    animation:'mqm-spin 0.7s linear infinite',
    display:'inline-block',
  },
  bottomPanels: {
    display:'grid',
    gridTemplateColumns:'minmax(0, 1fr) minmax(0, 1fr)',
    gap:16,
    marginTop:20,
  },
  aiInlinePanel: {
    background:'linear-gradient(180deg, #FDFBF8 0%, #fff 100%)',
    borderRadius:10,
    border:'1px solid rgba(107,28,42,0.18)',
    overflow:'hidden',
    display:'flex', flexDirection:'column',
  },
  aiInlineHeader: {
    display:'flex', justifyContent:'space-between', alignItems:'flex-start',
    padding:'14px 18px 10px',
    borderBottom:'1px solid #f0ebe3',
    gap:10,
  },
  aiInlineDate: { fontSize:11, color:'var(--text-secondary)', marginTop:2 },
  aiInlineRegen: {
    padding:'5px 10px', borderRadius:6,
    border:'1px solid rgba(107,28,42,0.3)', background:'#fff',
    color:'#6B1C2A', fontSize:11, fontWeight:700, cursor:'pointer',
    fontFamily:"'DM Sans',sans-serif", whiteSpace:'nowrap',
  },
  aiInlineCollapse: {
    width:26, height:26, borderRadius:6,
    border:'1px solid var(--border)', background:'#fff',
    color:'var(--text-secondary)', cursor:'pointer',
    display:'flex', alignItems:'center', justifyContent:'center',
    fontSize:14, lineHeight:1,
  },
  aiInlineBody: { padding:'14px 18px 18px' },
  aiInlineEmpty: { margin:0, fontSize:12.5, color:'var(--text-light)' },
  aiInlineSectionTitle: {
    fontFamily:"'DM Sans',sans-serif",
    fontSize:12, fontWeight:700, color:'#6B1C2A',
    textTransform:'uppercase', letterSpacing:'0.06em',
    margin:'0 0 4px',
  },
  aiInlineParagraph: {
    margin:0, fontSize:13, lineHeight:1.55, color:'var(--text-primary)',
  },
  manualRecsPanel: {
    background:'rgba(107,28,42,0.05)',
    border:'1px solid rgba(107,28,42,0.18)',
    borderRadius:8,
    padding:'10px 14px',
  },
  manualRecsTitle: {
    fontSize:11.5, fontWeight:700, color:'#6B1C2A',
    textTransform:'uppercase', letterSpacing:'0.05em',
    marginBottom:6,
  },
  manualRecsList: {
    margin:0, padding:0, listStyle:'none',
    display:'flex', flexDirection:'column', gap:6,
  },
  manualRecsItem: {
    display:'flex', gap:8, alignItems:'flex-start',
    fontSize:12.5, lineHeight:1.45, color:'var(--text-primary)',
  },
  manualRecsBullet: { color:'#C97B32', flexShrink:0 },
  workWeekendRow: {
    display:'flex', gap:18, flexWrap:'wrap',
    padding:'4px 2px 0',
  },
  workWeekendLabel: {
    display:'inline-flex', alignItems:'center', gap:8,
    fontSize:12.5, color:'var(--text-primary)',
    cursor:'pointer', userSelect:'none',
    fontFamily:"'DM Sans',sans-serif",
  },
  workWeekendCheck: {
    width:15, height:15, accentColor:'#6B1C2A', cursor:'pointer', margin:0,
  },
  subtaskOverlay: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(20,16,18,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 99999,
    padding: 24,
    overflowY: 'auto',
  },
  subtaskModal: {
    background: '#fff',
    borderRadius: 14,
    width: '100%',
    maxWidth: 520,
    maxHeight: 'calc(100vh - 48px)',
    overflow: 'auto',
    boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
    border: '1px solid rgba(107,28,42,0.15)',
    position: 'relative',
  },
  templateBanner: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    gap: 14, flexWrap: 'wrap',
    padding: '14px 18px',
    background: 'rgba(201,169,110,0.10)',
    border: '1px solid rgba(201,169,110,0.40)',
    borderRadius: 10,
    marginBottom: 14,
    fontFamily: "'DM Sans', sans-serif",
  },
  templateBannerText: {
    fontSize: 13, color: 'var(--text-primary)',
    flex: '1 1 320px', minWidth: 0,
  },
  templateBannerActions: { display: 'flex', gap: 8 },
  templateBannerSecondary: {
    padding: '7px 14px', borderRadius: 8,
    border: '1px solid var(--border)', background: '#fff',
    color: 'var(--text-secondary)', fontSize: 12, fontWeight: 600,
    cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
  },
  templateBannerPrimary: {
    padding: '7px 14px', borderRadius: 8,
    border: 'none', background: '#6B1C2A', color: '#fff',
    fontSize: 12, fontWeight: 700,
    cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
  },
  templateBannerClose: {
    width: 28, height: 28, borderRadius: 6,
    border: 'none', background: 'transparent',
    color: 'var(--text-secondary)', fontSize: 14, lineHeight: 1,
    cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  },
  confirmOverlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(20,16,18,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 99999,
    padding: 24,
  },
  confirmModal: {
    background: '#fff',
    borderRadius: 14,
    width: '100%',
    maxWidth: 440,
    padding: '22px 24px 18px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
    border: '1px solid rgba(184,59,59,0.20)',
    fontFamily: "'DM Sans', sans-serif",
  },
  confirmTitle: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: 22, fontWeight: 600, color: '#6B1C2A',
    margin: '0 0 8px',
  },
  confirmMessage: {
    margin: '0 0 18px',
    fontSize: 13.5, lineHeight: 1.55,
    color: 'var(--text-primary)',
  },
  confirmActions: {
    display: 'flex', justifyContent: 'flex-end', gap: 8,
  },
  confirmCancelBtn: {
    padding: '8px 16px', borderRadius: 8,
    border: '1px solid var(--border)', background: '#fff',
    color: 'var(--text-primary)', fontSize: 12.5, fontWeight: 600,
    cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
  },
  confirmDangerBtn: {
    padding: '8px 18px', borderRadius: 8, border: 'none',
    background: '#B83B3B', color: '#fff',
    fontSize: 12.5, fontWeight: 700,
    cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
  },
  templateDeleteBtn: {
    padding: '6px 11px', borderRadius: 6,
    border: '1px solid rgba(184,59,59,0.25)', background: '#fff',
    color: '#B83B3B', fontSize: 11.5, fontWeight: 600,
    cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
    whiteSpace: 'nowrap',
  },
  templateLoadBtn: {
    padding: '6px 11px', borderRadius: 6,
    border: '1px solid rgba(107,28,42,0.25)', background: '#fff',
    color: '#6B1C2A', fontSize: 11.5, fontWeight: 600,
    cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
    whiteSpace: 'nowrap',
  },
  expandArrow: {
    width:16, height:16, borderRadius:3,
    border:'none', background:'transparent',
    cursor:'pointer', color:'var(--text-secondary)',
    fontSize:9, lineHeight:1,
    display:'flex', alignItems:'center', justifyContent:'center',
    padding:0, flexShrink:0,
  },
  addSubtaskBtn: {
    padding:'2px 8px', borderRadius:10,
    border:'1px solid var(--border)', background:'#fff',
    color:'var(--wine)', fontSize:10, fontWeight:700,
    cursor:'pointer', fontFamily:"'DM Sans',sans-serif",
    flexShrink:0,
  },
  subtaskRow: {
    display:'flex', height:ROW_H, alignItems:'stretch',
    borderBottom:'1px solid #f3efe9',
    background:'#F9F5F5',
  },
  subtaskCheckbox: {
    width:18, height:18, padding:0, border:'none',
    background:'transparent', cursor:'pointer',
    fontSize:13, lineHeight:1, color:'var(--wine)',
    display:'flex', alignItems:'center', justifyContent:'center',
    flexShrink:0,
  },
  subtaskDeleteBtn: {
    width:18, height:18, padding:0, border:'none',
    background:'transparent', cursor:'pointer',
    fontSize:14, lineHeight:1, color:'var(--text-light)',
    display:'flex', alignItems:'center', justifyContent:'center',
    flexShrink:0, opacity:0.65,
  },
  weekendBadge: {
    display:'inline-flex', alignItems:'center', justifyContent:'center',
    width:16, height:16, borderRadius:4,
    background:'rgba(107,28,42,0.08)', color:'#6B1C2A',
    fontSize:9, fontWeight:800,
    flexShrink:0,
    letterSpacing:'-0.02em',
  },
  aiBtn: {
    display:'inline-flex', alignItems:'center', gap:7,
    padding:'7px 14px', borderRadius:6,
    border:'none',
    background:'linear-gradient(135deg, #6B1C2A 0%, #8C2A3D 100%)',
    color:'#fff',
    fontSize:12, fontWeight:700, cursor:'pointer',
    fontFamily:"'DM Sans',sans-serif", whiteSpace:'nowrap',
    boxShadow:'0 1px 4px rgba(107,28,42,0.25)',
  },
  aiOverlay: {
    position:'fixed', inset:0, background:'rgba(20,16,18,0.55)',
    display:'flex', alignItems:'center', justifyContent:'center',
    zIndex:10000, padding:24,
  },
  aiModal: {
    background:'#FDFBF8', borderRadius:14,
    width:'min(640px, 100%)', maxHeight:'90vh',
    display:'flex', flexDirection:'column',
    boxShadow:'0 20px 50px rgba(0,0,0,0.25)',
    border:'1px solid rgba(107,28,42,0.15)',
    fontFamily:"'DM Sans',sans-serif",
  },
  aiHeader: {
    display:'flex', justifyContent:'space-between', alignItems:'flex-start',
    padding:'18px 22px 12px', borderBottom:'1px solid #f0ebe3',
  },
  aiEyebrow: {
    fontSize:11, fontWeight:700, color:'#6B1C2A',
    textTransform:'uppercase', letterSpacing:'0.08em',
  },
  aiTitle: {
    fontFamily:"'Cormorant Garamond',serif",
    fontSize:22, fontWeight:600, color:'#6B1C2A',
    margin:'4px 0 2px',
  },
  aiSub: { fontSize:12, color:'var(--text-secondary)' },
  aiClose: {
    border:'none', background:'transparent',
    fontSize:18, cursor:'pointer', color:'var(--text-secondary)',
    padding:4, lineHeight:1,
  },
  aiBody: { padding:'16px 22px', overflowY:'auto', flex:1 },
  aiSectionTitle: {
    fontFamily:"'Cormorant Garamond',serif",
    fontSize:16, fontWeight:600, color:'#6B1C2A',
    margin:'0 0 6px',
  },
  aiParagraph: {
    fontSize:13.5, color:'var(--text-primary)',
    margin:0, lineHeight:1.55,
  },
  aiLoading: {
    display:'flex', alignItems:'center', gap:10,
    fontSize:13, color:'var(--text-secondary)',
    padding:'24px 0',
  },
  aiError: {
    padding:'12px 14px', borderRadius:8,
    background:'rgba(184,59,59,0.06)', color:'#B83B3B',
    fontSize:12.5, border:'1px solid rgba(184,59,59,0.2)',
  },
  aiFooter: {
    display:'flex', justifyContent:'flex-end', gap:8,
    padding:'12px 22px', borderTop:'1px solid #f0ebe3', background:'#fff',
    borderBottomLeftRadius:14, borderBottomRightRadius:14,
  },
  aiSecondaryBtn: {
    padding:'8px 14px', borderRadius:8,
    border:'1px solid var(--border)', background:'#fff',
    color:'var(--text-primary)', fontSize:12, fontWeight:600,
    cursor:'pointer', fontFamily:"'DM Sans',sans-serif",
  },
  aiPrimaryBtn: {
    padding:'8px 16px', borderRadius:8, border:'none',
    background:'#6B1C2A', color:'#fff',
    fontSize:12, fontWeight:700, cursor:'pointer',
    fontFamily:"'DM Sans',sans-serif",
  },

  /* Card & layout */
  card: { background:'#fff', borderRadius:10, border:'1px solid var(--border)', overflow:'hidden' },
  loadingMsg: { padding:'40px 24px', textAlign:'center', color:'var(--text-light)', fontSize:14 },
  ganttWrap: { display:'flex', width:'100%' },
  leftPanel: { width:LEFT_W, minWidth:LEFT_W, flexShrink:0, borderRight:'2px solid #6B1C2A', background:'#fff' },
  rightPanel: { flex:1, minWidth:0, overflowX:'auto', overflowY:'hidden', cursor:'grab' },

  /* Left column headers */
  leftHeaderRow: { display:'flex', height:HDR_TOTAL_H, background:'#6B1C2A', color:'#fff', borderBottom:'1px solid var(--border)' },
  leftHeaderCell: { display:'flex', alignItems:'center', padding:'0 10px', fontSize:10, fontWeight:700, letterSpacing:'0.06em', textTransform:'uppercase', borderRight:'1px solid rgba(255,255,255,0.15)', color:'#fff' },

  /* Category header */
  catHeader: { height:CAT_H, display:'flex', alignItems:'center', padding:'0 14px', fontSize:11, fontWeight:700, color:'#fff', letterSpacing:'0.04em', textTransform:'uppercase', borderBottom:'1px solid rgba(0,0,0,0.15)', fontFamily:"'DM Sans',sans-serif" },

  /* Data rows */
  dataRow: { display:'flex', height:ROW_H, borderBottom:'1px solid #f3efe9', background:'#fff' },
  dataCell: { display:'flex', alignItems:'center', padding:'0 10px', fontSize:12, color:'var(--text-primary)', borderRight:'1px solid #f3efe9', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' },

  /* Modals */
  overlay: { position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, backdropFilter:'blur(2px)' },
  modal: { background:'#fff', borderRadius:14, width:'100%', maxWidth:560, maxHeight:'90vh', overflow:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.15)' },
  modalHeader: { display:'flex', justifyContent:'space-between', alignItems:'flex-start', padding:'22px 26px 0', gap:16 },
  modalTitle: { fontFamily:"'Cormorant Garamond',serif", fontSize:22, fontWeight:600, color:'var(--wine)', margin:0 },
  modalSubtitle: { fontSize:12, color:'var(--text-secondary)', marginTop:3 },
  closeBtn: { width:30, height:30, borderRadius:8, border:'1px solid var(--border)', background:'transparent', cursor:'pointer', fontSize:13, color:'var(--text-secondary)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 },
  journalBody: { padding:'14px 26px 26px' },
  journalEntries: { display:'flex', flexDirection:'column', gap:10, maxHeight:260, overflowY:'auto' },
  journalEntry: { background:'#FDFBF8', border:'1px solid var(--border)', borderRadius:10, padding:'10px 14px' },
  emptyMsg: { fontSize:13, color:'var(--text-light)', textAlign:'center', padding:'14px 22px 22px', margin:0 },
  form: { padding:'18px 26px 26px', display:'flex', flexDirection:'column', gap:14 },
  fieldRow: { display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 },
  fieldGroup: { display:'flex', flexDirection:'column', gap:5 },
  fieldLabel: { fontSize:11, fontWeight:600, color:'var(--text-secondary)', letterSpacing:'0.02em' },
  input: { padding:'8px 11px', borderRadius:7, border:'1px solid var(--border)', fontSize:13, fontFamily:"'DM Sans',sans-serif", color:'var(--text-primary)', background:'#FDFBF8', outline:'none', width:'100%', boxSizing:'border-box' },
  textarea: { padding:'9px 12px', borderRadius:8, border:'1px solid var(--border)', fontSize:13, fontFamily:"'DM Sans',sans-serif", color:'var(--text-primary)', background:'#FDFBF8', outline:'none', width:'100%', boxSizing:'border-box', resize:'vertical', lineHeight:1.5 },
  modalActions: { display:'flex', justifyContent:'flex-end', alignItems:'center', gap:10, marginTop:4 },
  cancelBtn: { padding:'8px 18px', borderRadius:7, border:'1px solid var(--border)', background:'transparent', fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:"'DM Sans',sans-serif", color:'var(--text-secondary)' },
  submitBtn: { padding:'8px 22px', borderRadius:7, border:'none', background:'#6B1C2A', color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:"'DM Sans',sans-serif" },
  deleteBtn: { padding:'8px 18px', borderRadius:7, border:'1px solid rgba(184,59,59,0.3)', background:'rgba(184,59,59,0.08)', color:'#B83B3B', fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:"'DM Sans',sans-serif" },

  /* Mobile (< 640px) modal -> full-screen sheet overrides. Only merged when isMobile. */
  modalMobile: { ...sheet.panel, display:'flex', flexDirection:'column' },
  modalHeaderMobile: { padding:'12px 16px', alignItems:'center', borderBottom:'1px solid #f0ebe3', flexShrink:0, ...sheet.header },
  formMobile: { padding:'16px 16px 0', flex:'1 0 auto' },
  fieldRowMobile: { gridTemplateColumns:'minmax(0, 1fr)' },
  modalActionsMobile: { padding:'12px 16px', margin:'auto -16px 0', borderTop:'1px solid #f0ebe3', flexWrap:'wrap', ...sheet.footer },
  btnMobile: { minHeight:TAP_TARGET, flex:'1 1 auto' },
  journalBodyMobile: { padding:'14px 16px 16px' },
  journalNewRowMobile: { flexWrap:'wrap', gap:10 },
  journalEntriesMobile: { maxHeight:'none', overflowY:'visible' },

  /* Phase 3 — compact (< 1024px) / mobile (< 640px) Gantt overrides. Never used on desktop. */
  tapCompact: { minHeight:TAP_TARGET, minWidth:TAP_TARGET },
  textMobile: { fontSize:15 },
  backBarMobile: { flexWrap:'wrap', gap:8 },
  excelHeaderMobile: { padding:'14px', gap:12 },
  excelHeaderTopMobile: { flexDirection:'column', alignItems:'stretch', gap:12 },
  hdrTitleMobile: { fontSize:18, lineHeight:1.25 },
  hdrRowMobile: { flexDirection:'column', alignItems:'flex-start', justifyContent:'flex-start', gap:4 },
  hdrLabelMobile: { minWidth:0 },
  hdrValueMobile: { fontSize:15, minWidth:0, maxWidth:'100%', overflowWrap:'anywhere', boxSizing:'border-box' },
  hdrDurationMobile: { fontSize:14, whiteSpace:'normal' },
  hdrRightMobile: { alignItems:'flex-start' },
  projectSelectMobile: { flex:'1 1 100%', width:'100%', minWidth:0, minHeight:TAP_TARGET },
  searchWrapMobile: { flex:'1 1 100%', width:'100%', minWidth:0 },
  searchInputMobile: { minHeight:TAP_TARGET, paddingRight:TAP_TARGET },
  searchClearBtnMobile: { right:0, width:TAP_TARGET, height:TAP_TARGET, borderRadius:TAP_TARGET / 2, background:'transparent', fontSize:13 },
  navRangeMobile: { flex:'1 1 100%', order:1, fontSize:13 },
  bottomPanelsCompact: { gridTemplateColumns:'minmax(0, 1fr)' },
  rightPanelCompact: { cursor:'auto', WebkitOverflowScrolling:'touch', overscrollBehaviorX:'contain', touchAction:'pan-x pan-y' },
  leftPanelCompact: { position:'relative', zIndex:2, boxShadow:'2px 0 6px rgba(0,0,0,0.06)' },
  catHeaderCompact: { display:'block', padding:'0 8px', lineHeight:`${CAT_H}px`, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' },
  expandArrowCompact: { width:28, height:ROW_H, fontSize:10 },
  progPillCompact: { fontSize:11, fontWeight:800, padding:'3px 6px', borderRadius:10, minWidth:38, textAlign:'center', lineHeight:1.15 },
  sheetDate: { fontSize:14, fontWeight:600, color:'var(--wine)', textTransform:'capitalize' },
  sheetDetails: { display:'flex', flexDirection:'column', border:'1px solid var(--border)', borderRadius:10, background:'#fff', marginBottom:4 },
  sheetDetailRow: { display:'flex', justifyContent:'space-between', alignItems:'center', gap:12, padding:'10px 14px', borderBottom:'1px solid #f3efe9' },
  sheetDetailLabel: { fontSize:13, fontWeight:700, color:'var(--text-light)', textTransform:'uppercase', letterSpacing:'0.06em', flexShrink:0 },
  sheetDetailValue: { fontSize:15, fontWeight:600, color:'var(--text-primary)', textAlign:'right', minWidth:0, overflowWrap:'anywhere' },
  sheetActionBtn: { minHeight:48, width:'100%', padding:'10px 16px', borderRadius:10, border:'1px solid var(--border)', background:'#fff', color:'var(--text-primary)', fontSize:15, fontWeight:600, cursor:'pointer', fontFamily:"'DM Sans',sans-serif", textAlign:'left' },
  sheetActionPrimary: { background:'#6B1C2A', borderColor:'#6B1C2A', color:'#fff' },
  sheetActionDanger: { color:'#B83B3B', borderColor:'rgba(184,59,59,0.3)', background:'rgba(184,59,59,0.06)' },

  /* Compact navigation: native swipe, week snap, Cronograma | Lista. Never used on desktop. */
  rightPanelSnap: { overflowX:'auto', scrollSnapType:'x proximity' },
  weekSnapMarker: { position:'absolute', top:0, width:1, height:1, scrollSnapAlign:'start', pointerEvents:'none' },
  compactBar: { display:'flex', flexDirection:'column', gap:8, marginBottom:10 },
  compactNavRow: { display:'flex', alignItems:'center', gap:8 },
  compactNavBtn: { minWidth:TAP_TARGET, minHeight:TAP_TARGET, borderRadius:8, border:'1px solid var(--border)', background:'#fff', color:'var(--wine)', fontSize:14, cursor:'pointer', display:'inline-flex', alignItems:'center', justifyContent:'center', flexShrink:0 },
  compactRange: { flex:1, minWidth:0, textAlign:'center', fontSize:15, fontWeight:700, color:'var(--wine)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' },
  compactTodayBtn: { minHeight:TAP_TARGET, padding:'0 16px', borderRadius:8, border:'1.5px solid '+CLR_RED, background:'#fff', color:CLR_RED, fontSize:15, fontWeight:700, cursor:'pointer', fontFamily:"'DM Sans',sans-serif", flexShrink:0 },
  segGroup: { display:'flex', padding:3, gap:3, borderRadius:10, border:'1px solid var(--border)', background:'#fff' },
  segBtn: { flex:1, minHeight:TAP_TARGET - 6, padding:'0 12px', border:'none', borderRadius:7, background:'transparent', color:'var(--text-secondary)', fontSize:15, fontWeight:600, cursor:'pointer', fontFamily:"'DM Sans',sans-serif" },
  segBtnActive: { background:'#6B1C2A', color:'#fff' },
  listWrap: { display:'flex', flexDirection:'column', gap:14, minWidth:0 },
  listSection: { display:'flex', flexDirection:'column', gap:8, minWidth:0 },
  listCatHeader: { color:'#fff', fontSize:13, fontWeight:700, letterSpacing:'0.04em', textTransform:'uppercase', padding:'8px 12px', borderRadius:8, overflowWrap:'anywhere' },
  listCard: { display:'flex', flexDirection:'column', gap:6, padding:'12px 14px', background:'#fff', border:'1px solid var(--border)', borderRadius:10, cursor:'pointer', minWidth:0, boxSizing:'border-box', width:'100%' },
  listCardSub: { marginLeft:18, width:'calc(100% - 18px)', background:'#FDFBF8', borderStyle:'dashed' },
  listCardTop: { display:'flex', alignItems:'center', gap:10, minWidth:0 },
  listDot: { width:10, height:10, borderRadius:'50%', flexShrink:0 },
  listName: { flex:1, minWidth:0, fontSize:15, color:'var(--text-primary)', lineHeight:1.35, overflowWrap:'anywhere' },
  listChevron: { width:TAP_TARGET, height:TAP_TARGET, margin:'-10px -10px -10px 0', border:'none', background:'transparent', color:'var(--wine)', fontSize:18, cursor:'pointer', flexShrink:0, display:'inline-flex', alignItems:'center', justifyContent:'center' },
  listMetaRow: { display:'flex', alignItems:'center', gap:8, minWidth:0 },
  listAvatar: { width:24, height:24, borderRadius:'50%', color:'#fff', fontSize:10, fontWeight:700, display:'inline-flex', alignItems:'center', justifyContent:'center', flexShrink:0 },
  listWho: { fontSize:15, color:'var(--text-secondary)', minWidth:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' },
  listDates: { fontSize:15, color:'var(--text-primary)' },
  listProgressRow: { display:'flex', alignItems:'center', gap:10 },
  listBar: { flex:1, height:8, borderRadius:4, background:'#F0EBE3', overflow:'hidden' },
  listBarFill: { height:'100%', borderRadius:4 },
  listPct: { fontSize:15, fontWeight:700, color:'var(--text-primary)', flexShrink:0 },
};
